import React, { useRef, useEffect, useState, useLayoutEffect, useCallback } from 'react';
import * as THREE from 'three';

import { 
  initializeQuantumOptimization,
  launchQuantumOptimizer,
  analyzeThreadQuantumMetrics,
  calculateOptimizationPotential,
  calculateThreadHealth,
  generateShortTermGoals,
  generateMediumTermGoals,
  generateLongTermGoals,
  calculateExpectedImpact,
  assessImplementationComplexity,
  checkOptimizationProgress,
  adjustRecommendations,
  calculateCurrentQuantumMetrics,
  updateMetricsDisplay,
  triggerQuantumInterferenceEvents,
  identifyCriticalLifecycles,
  applyLifecycleOptimization,
  updateSessionMetrics,
  getSelectedRecommendations,
  executeRecommendation,
  generateOptimizationReport,
  generateComprehensiveReport,
  generateFinalOptimizationReport,
  saveOptimizationSession
} from './QuantumOptimizerUtils';


const PLMVisualization = () => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const animationRef = useRef(null);
  const plmCoreRef = useRef(null);
  const constellationsRef = useRef([]);
  const digitalThreadsRef = useRef([]);
  const goldenThreadsRef = useRef([]);
  const starfieldRef = useRef(null);
  const particleSpheresRef = useRef([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [flowSpeed, setFlowSpeed] = useState(1);
  const [threadIntensity, setThreadIntensity] = useState(1);
  const [glowIntensity, setGlowIntensity] = useState(1);
  const [showLabels, setShowLabels] = useState(true);
  const [isExplosionView, setIsExplosionView] = useState(false);
  const [selectedThreads, setSelectedThreads] = useState(new Set());
  const [goldenThreadType, setGoldenThreadType] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showThreadPanel, setShowThreadPanel] = useState(true);
  const [customThreadModal, setCustomThreadModal] = useState(false);
  const [newThreadName, setNewThreadName] = useState('');
  const [newThreadColor, setNewThreadColor] = useState('#1E90FF');
  const [selectedFunctions, setSelectedFunctions] = useState(new Set());
  const [rotationSpeed, setRotationSpeed] = useState(0.0);
  const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  
  const [labelFontSize, setLabelFontSize] = useState(20);
const sphereLabelsRef = useRef([]);
const [showTopPanel, setShowTopPanel] = useState(true);

// Add these new state variables near your other state declarations
const [showOnlyThreadConnectedLabels, setShowOnlyThreadConnectedLabels] = useState(false);
const [threadConnectedLabels, setThreadConnectedLabels] = useState(new Set());

  
  const [cameraZoom, setCameraZoom] = useState(1);

  // Add new state for magnifier effect
  const [magnifier, setMagnifier] = useState({
    visible: false,
    content: '',
    x: 0,
    y: 0,
    scale: 1
  });



  // Define all functions, domains, and systems
  const allFunctions = [
    'Marketing', 'Sales', 'Engineering', 'QA', 'Regulatory', 'R&D', 'Mfg Eng', 
    'Test', 'Certification', 'SCM', 'Service', 'Field Ops', 'Warranty', 'Ops', 
    'Finance', 'PMO', 'Compliance', 'HR', 'IT Sec', 'Legal', 'Data Science', 
    'Product Mgmt', 'UX', 'ESG', 'Exec Mgmt', 'Maintenance'
  ];

  const allDomains = [
    'Sys Eng', 'Mechanical', 'Electrical', 'Software', 'MCAD', 'ECAD', 'MBSE',
    'Structural', 'Thermal', 'CFD', 'Controls', 'eBOM', 'mBOM', 'sBOM', 'Variants',
    'Process', 'Tooling', 'Plant Eng', 'Mechatronics', 'Telemetry', 'Firmware',
    'Eco materials', 'Recycling', 'Cost allocations', 'Templates', 'Best Practices',
    'Secure MCAD', 'Secure ECAD', 'Secure SW', 'Aero certs', 'Auto certs', 'Med certs',
    'Milestones', 'Deliverables', 'FEA', 'Multiphysics', 'Options', 'Ergonomics',
    'AR/VR', 'HMI', 'Asset health', 'AI MCAD', 'AI ECAD', 'AI SW', 'UX design',
    'ESG impacts'
  ];

  const allSystems = [
    'RM tools', 'PLM', 'ALM', 'Compliance DB', 'CAD/CAE', 'MBSE tools', 'CAE',
    'HPC', 'SDM', 'ERP', 'MES', 'Configurator', 'SCM tools', 'SRM', 'CRM', 'EAM',
    'IoT platform', 'Data lake', 'LCA', 'ESG dashboards', 'BI tools', 'KM systems',
    'AI/ML repos', 'IAM', 'Supplier portals', 'PPM tools', 'HPC schedulers', 'LMS',
    'UX tools', 'SCADA', 'QMS', 'TMS', 'WMS', 'DMS', 'BPM', 'MDM', 'APM'
  ];

  // Departments with their functional areas, domains, and systems
const departments = [
  // Domains (First sphere - no color, radius 25)
  { id: 1, name: 'Mechanical Engineering', function: 'Engineering', domain: 'MCAD', system: 'CAD/CAE', position: [25, 0, 0], type: 'domain' },
  { id: 2, name: 'Electrical Engineering', function: 'Engineering', domain: 'ECAD', system: 'ECAD tools', position: [0, 25, 0], type: 'domain' },
  { id: 3, name: 'Software Development', function: 'Engineering', domain: 'SW', system: 'ALM', position: [-25, 0, 0], type: 'domain' },
  { id: 4, name: 'Systems Engineering', function: 'Engineering', domain: 'MBSE', system: 'MBSE tools', position: [0, -25, 0], type: 'domain' },
  { id: 5, name: 'Simulation Center', function: 'Engineering', domain: 'Simulation', system: 'CAE', position: [18, 18, 0], type: 'domain' },
  { id: 6, name: 'Process Engineering', function: 'Mfg Eng', domain: 'Process', system: 'MES', position: [-18, 18, 0], type: 'domain' },
  { id: 7, name: 'Structural Engineering', function: 'Engineering', domain: 'Structural', system: 'CAE', position: [18, -18, 0], type: 'domain' },
  { id: 8, name: 'Thermal Analysis', function: 'Engineering', domain: 'Thermal', system: 'CAE', position: [-18, -18, 0], type: 'domain' },
  { id: 9, name: 'CFD Analysis', function: 'Engineering', domain: 'CFD', system: 'CAE', position: [0, 18, 18], type: 'domain' },
  { id: 10, name: 'Controls Engineering', function: 'Engineering', domain: 'Controls', system: 'MBSE tools', position: [0, -18, -18], type: 'domain' },
  { id: 11, name: 'BOM Management', function: 'Engineering', domain: 'eBOM', system: 'PLM', position: [18, 0, 18], type: 'domain' },
  { id: 12, name: 'Manufacturing BOM', function: 'Mfg Eng', domain: 'mBOM', system: 'PLM', position: [-18, 0, -18], type: 'domain' },
  { id: 13, name: 'Service BOM', function: 'Service', domain: 'sBOM', system: 'PLM', position: [0, 18, -18], type: 'domain' },
  { id: 14, name: 'Variant Management', function: 'Product Mgmt', domain: 'Variants', system: 'PLM', position: [0, -18, 18], type: 'domain' },
  { id: 15, name: 'Tooling Design', function: 'Mfg Eng', domain: 'Tooling', system: 'CAD/CAE', position: [12, 12, 12], type: 'domain' },
  { id: 16, name: 'Plant Engineering', function: 'Mfg Eng', domain: 'Plant Eng', system: 'CAD/CAE', position: [-12, -12, -12], type: 'domain' },
  { id: 17, name: 'Mechatronics', function: 'Engineering', domain: 'Mechatronics', system: 'MBSE tools', position: [12, -12, 12], type: 'domain' },
  { id: 18, name: 'Telemetry Systems', function: 'Ops', domain: 'Telemetry', system: 'IoT platform', position: [-12, 12, -12], type: 'domain' },
  { id: 19, name: 'Firmware Development', function: 'Engineering', domain: 'Firmware', system: 'ALM', position: [15, 10, 5], type: 'domain' },
  { id: 20, name: 'Sustainability Engineering', function: 'ESG', domain: 'Eco materials', system: 'LCA', position: [-15, -10, -5], type: 'domain' },
  
  // Functions (Second sphere - light transparent red, radius 40)
  { id: 21, name: 'Marketing', function: 'Marketing', domain: 'Commercial', system: 'CRM', position: [40, 0, 0], type: 'function' },
  { id: 22, name: 'Sales', function: 'Sales', domain: 'Commercial', system: 'CRM', position: [0, 40, 0], type: 'function' },
  { id: 23, name: 'R&D', function: 'R&D', domain: 'Innovation', system: 'MBSE tools', position: [-40, 0, 0], type: 'function' },
  { id: 24, name: 'Manufacturing Eng', function: 'Mfg Eng', domain: 'Process', system: 'MES', position: [0, -40, 0], type: 'function' },
  { id: 25, name: 'Quality Assurance', function: 'QA', domain: 'Quality', system: 'QM systems', position: [28, 28, 0], type: 'function' },
  { id: 26, name: 'Regulatory Compliance', function: 'Regulatory', domain: 'Compliance', system: 'Compliance DB', position: [-28, 28, 0], type: 'function' },
  { id: 27, name: 'Supply Chain Mgmt', function: 'SCM', domain: 'Logistics', system: 'SCM tools', position: [-28, -28, 0], type: 'function' },
  { id: 28, name: 'Procurement', function: 'Procurement', domain: 'Sourcing', system: 'SRM', position: [28, -28, 0], type: 'function' },
  { id: 29, name: 'Customer Service', function: 'Service', domain: 'Support', system: 'CRM', position: [35, 15, 0], type: 'function' },
  { id: 30, name: 'Field Operations', function: 'Field Ops', domain: 'Service', system: 'EAM', position: [-35, 15, 0], type: 'function' },
  { id: 31, name: 'Warranty Management', function: 'Warranty', domain: 'Service', system: 'CRM', position: [-35, -15, 0], type: 'function' },
  { id: 32, name: 'Operations', function: 'Ops', domain: 'Production', system: 'MES', position: [35, -15, 0], type: 'function' },
  { id: 33, name: 'Testing', function: 'Test', domain: 'Validation', system: 'CAE', position: [25, 25, 15], type: 'function' },
  { id: 34, name: 'Certification', function: 'Certification', domain: 'Compliance', system: 'Compliance DB', position: [-25, -25, -15], type: 'function' },
  { id: 35, name: 'Data Science', function: 'Data Science', domain: 'Analytics', system: 'Data lake', position: [20, -20, 20], type: 'function' },
  { id: 36, name: 'Product Management', function: 'Product Mgmt', domain: 'Commercial', system: 'PLM', position: [-20, 20, -20], type: 'function' },
  { id: 37, name: 'User Experience', function: 'UX', domain: 'UX design', system: 'UX tools', position: [15, -25, 20], type: 'function' },
  { id: 38, name: 'ESG Office', function: 'ESG', domain: 'Sustainability', system: 'ESG dashboards', position: [-15, 25, -20], type: 'function' },
  { id: 39, name: 'Executive Management', function: 'Exec Mgmt', domain: 'Strategy', system: 'BI tools', position: [10, 30, 20], type: 'function' },
  { id: 40, name: 'Maintenance', function: 'Maintenance', domain: 'Asset health', system: 'EAM', position: [-10, -30, -20], type: 'function' },
  
  // Systems (Third sphere - light transparent green, radius 55)
  { id: 41, name: 'ERP System', function: 'Finance', domain: 'Financial', system: 'ERP', position: [55, 0, 0], type: 'system' },
  { id: 42, name: 'MES System', function: 'Mfg Eng', domain: 'Process', system: 'MES', position: [0, 55, 0], type: 'system' },
  { id: 43, name: 'CRM System', function: 'Sales', domain: 'Commercial', system: 'CRM', position: [-55, 0, 0], type: 'system' },
  { id: 44, name: 'EAM System', function: 'Maintenance', domain: 'Service', system: 'EAM', position: [0, -55, 0], type: 'system' },
  { id: 45, name: 'IoT Platform', function: 'Ops', domain: 'Telemetry', system: 'IoT platform', position: [39, 39, 0], type: 'system' },
  { id: 46, name: 'Data Lake', function: 'Data Science', domain: 'Analytics', system: 'Data lake', position: [-39, 39, 0], type: 'system' },
  { id: 47, name: 'BI Tools', function: 'Exec Mgmt', domain: 'Analytics', system: 'BI tools', position: [-39, -39, 0], type: 'system' },
  { id: 48, name: 'SCM Tools', function: 'SCM', domain: 'Logistics', system: 'SCM tools', position: [39, -39, 0], type: 'system' },
  { id: 49, name: 'IAM System', function: 'IT Sec', domain: 'Technology', system: 'IAM', position: [45, -25, 0], type: 'system' },
  { id: 50, name: 'Supplier Portal', function: 'Procurement', domain: 'Sourcing', system: 'Supplier portals', position: [45, 25, 0], type: 'system' },
  { id: 51, name: 'HPC Cluster', function: 'Engineering', domain: 'Simulation', system: 'HPC', position: [-45, 25, 0], type: 'system' },
  { id: 52, name: 'LCA Tools', function: 'ESG', domain: 'Sustainability', system: 'LCA', position: [-45, -25, 0], type: 'system' },
  { id: 53, name: 'Requirements Management', function: 'Engineering', domain: 'Sys Eng', system: 'RM tools', position: [30, 40, 20], type: 'system' },
  { id: 54, name: 'Compliance Database', function: 'Regulatory', domain: 'Compliance', system: 'Compliance DB', position: [-30, -40, -20], type: 'system' },
  { id: 55, name: 'SDM Repository', function: 'Engineering', domain: 'Simulation', system: 'SDM', position: [20, 45, -20], type: 'system' },
  { id: 56, name: 'Configurator', function: 'Sales', domain: 'Variants', system: 'Configurator', position: [-20, -45, 20], type: 'system' },
  { id: 57, name: 'SRM System', function: 'Procurement', domain: 'Sourcing', system: 'SRM', position: [25, -40, 25], type: 'system' },
  { id: 58, name: 'KM System', function: 'Engineering', domain: 'Best Practices', system: 'KM systems', position: [-25, 40, -25], type: 'system' },
  { id: 59, name: 'AI/ML Repository', function: 'Data Science', domain: 'Analytics', system: 'AI/ML repos', position: [35, -35, 25], type: 'system' },
  { id: 60, name: 'LMS Platform', function: 'HR', domain: 'Training', system: 'LMS', position: [-35, 35, -25], type: 'system' }
];

  // All 22 digital threads with their connections
  const [threadTypes, setThreadTypes] = useState([
    { 
      id: 1,
      name: "Requirements & Compliance", 
      color: 0x1E90FF, 
      speed: 0.003, 
      description: "Connects marketing, regulatory, and engineering requirements across the product lifecycle.",
      connects: ['Marketing', 'Sales', 'Engineering', 'QA', 'Regulatory'],
      domains: ['Sys Eng', 'Mechanical', 'Electrical', 'Software'],
      systems: ['RM tools', 'PLM', 'ALM', 'Compliance DB']
    },
    { 
      id: 2,
      name: "Design & Engineering", 
      color: 0x32CD32, 
      speed: 0.002, 
      description: "Integrates engineering design data across mechanical, electrical and software domains.",
      connects: ['Engineering', 'R&D', 'Mfg Eng', 'QA'],
      domains: ['MCAD', 'ECAD', 'SW', 'MBSE'],
      systems: ['CAD/CAE', 'PLM', 'MBSE tools']
    },
    { 
      id: 3,
      name: "Simulation & Validation", 
      color: 0xFF4500, 
      speed: 0.004, 
      description: "Connects simulation data with validation and certification processes.",
      connects: ['Engineering', 'Test', 'QA', 'Certification'],
      domains: ['Structural', 'Thermal', 'CFD', 'Controls'],
      systems: ['CAE', 'PLM', 'HPC', 'SDM']
    },
    { 
      id: 4,
      name: "Config & BOM Mgmt", 
      color: 0x9370DB, 
      speed: 0.0025, 
      description: "Manages product configurations and bill of materials across the lifecycle.",
      connects: ['Engineering', 'Mfg Eng', 'SCM', 'Service'],
      domains: ['eBOM', 'mBOM', 'sBOM', 'Variants'],
      systems: ['PLM', 'ERP', 'MES', 'Configurator']
    },
    { 
      id: 5,
      name: "Change & Governance", 
      color: 0xFF1493, 
      speed: 0.0035, 
      description: "Manages engineering changes and governance processes across domains.",
      connects: ['Engineering', 'QA', 'SCM', 'PMO', 'Compliance'],
      domains: ['MCAD', 'ECAD', 'SW'],
      systems: ['PLM Change Mgmt', 'ERP workflow']
    },
    { 
      id: 6,
      name: "Manufacturing Planning", 
      color: 0x00CED1, 
      speed: 0.0028, 
      description: "Connects manufacturing planning with execution systems and processes.",
      connects: ['Mfg Eng', 'Ops', 'SCM'],
      domains: ['Process', 'Tooling', 'Plant Eng'],
      systems: ['PLM Mfg', 'MES', 'ERP']
    },
    { 
      id: 7,
      name: "Supply Chain & Procurement", 
      color: 0xFF6347, 
      speed: 0.0022, 
      description: "Integrates supply chain and procurement processes with product data.",
      connects: ['SCM', 'Procurement', 'Engineering', 'Finance'],
      domains: ['Mech', 'Electronics', 'SW sourcing'],
      systems: ['ERP', 'SCM tools', 'PLM', 'SRM']
    },
    { 
      id: 8,
      name: "Service & Support", 
      color: 0x7CFC00, 
      speed: 0.0032, 
      description: "Connects service and support data with product design and manufacturing.",
      connects: ['Service', 'Field Ops', 'Warranty'],
      domains: ['Mechatronics', 'ECAD', 'SW patches'],
      systems: ['CRM', 'EAM', 'PLM Service BOM']
    },
    { 
      id: 9,
      name: "IoT & Usage Feedback", 
      color: 0x8A2BE2, 
      speed: 0.0042, 
      description: "Integrates IoT data and usage feedback into product development.",
      connects: ['Ops', 'Service', 'R&D', 'QA'],
      domains: ['Telemetry', 'Firmware', 'Mech data'],
      systems: ['IoT platform', 'PLM', 'ERP', 'Data lake']
    },
    { 
      id: 10,
      name: "Sustainability & Circular", 
      color: 0x20B2AA, 
      speed: 0.0022, 
      description: "Connects sustainability data with product design and lifecycle management.",
      connects: ['ESG', 'Compliance', 'SCM'],
      domains: ['Eco materials', 'Recycling'],
      systems: ['PLM', 'LCA', 'ERP', 'ESG dashboards']
    },
    { 
      id: 11,
      name: "Business & Financial", 
      color: 0xDA70D6, 
      speed: 0.0025, 
      description: "Integrates business and financial data with product lifecycle information.",
      connects: ['Finance', 'PMO', 'Exec Mgmt'],
      domains: ['Cost allocations'],
      systems: ['ERP Finance', 'PLM Cost', 'BI tools']
    },
    { 
      id: 12,
      name: "Knowledge & Learning", 
      color: 0x32CD32, 
      speed: 0.0028, 
      description: "Connects knowledge management with product development and training.",
      connects: ['Engineering', 'QA', 'HR', 'Mfg', 'Service'],
      domains: ['Templates', 'Best Practices'],
      systems: ['PLM KB', 'KM systems', 'AI/ML repos']
    },
    { 
      id: 13,
      name: "Cybersecurity & Trust", 
      color: 0xFF0000, 
      speed: 0.0035, 
      description: "Integrates cybersecurity across product design and lifecycle management.",
      connects: ['IT Sec', 'Engineering', 'SCM', 'Compliance'],
      domains: ['Secure MCAD', 'Secure ECAD', 'Secure SW'],
      systems: ['IAM', 'PLM vaults', 'Supplier portals']
    },
    { 
      id: 14,
      name: "Certification & Regulatory", 
      color: 0x6495ED, 
      speed: 0.0032, 
      description: "Manages certifications and regulatory compliance across product lifecycle.",
      connects: ['QA', 'Compliance', 'Engineering', 'Legal'],
      domains: ['Aero certs', 'Auto certs', 'Med certs'],
      systems: ['PLM cert mgmt', 'Compliance DB']
    },
    { 
      id: 15,
      name: "Program & Project Mgmt", 
      color: 0xFFD700, 
      speed: 0.0022, 
      description: "Connects program and project management with product development.",
      connects: ['PMO', 'Engineering', 'SCM', 'Finance'],
      domains: ['Milestones', 'Deliverables'],
      systems: ['PLM schedule', 'PPM tools', 'ERP']
    },
    { 
      id: 16,
      name: "Simulation Data Mgmt", 
      color: 0x4B0082, 
      speed: 0.0038, 
      description: "Manages simulation data across engineering domains and processes.",
      connects: ['Engineering', 'Test', 'QA', 'IT Sec'],
      domains: ['FEA', 'CFD', 'Multiphysics'],
      systems: ['SDM repos', 'PLM', 'HPC schedulers']
    },
    { 
      id: 17,
      name: "Portfolio & Variant Mgmt", 
      color: 0x1E90FF, 
      speed: 0.0025, 
      description: "Manages product portfolios and variants across domains.",
      connects: ['Product Mgmt', 'Engineering', 'Mfg', 'Sales'],
      domains: ['Variants', 'Options'],
      systems: ['PLM Config', 'ERP variants']
    },
    { 
      id: 18,
      name: "Human Factors & Training", 
      color: 0x8B4513, 
      speed: 0.0032, 
      description: "Integrates human factors and training with product design.",
      connects: ['HR', 'Ops', 'Engineering', 'Service'],
      domains: ['Ergonomics', 'AR/VR', 'HMI'],
      systems: ['AR/VR tools', 'PLM Training', 'LMS']
    },
    { 
      id: 19,
      name: "Asset Lifecycle (EAM)", 
      color: 0x7CFC00, 
      speed: 0.0028, 
      description: "Connects asset lifecycle management with product data.",
      connects: ['Ops', 'Maintenance', 'Service', 'Engineering'],
      domains: ['Asset health', 'Firmware'],
      systems: ['PLM as-maintained', 'EAM', 'ERP', 'IoT']
    },
    { 
      id: 20,
      name: "AI & Analytics", 
      color: 0x9370DB, 
      speed: 0.0035, 
      description: "Integrates AI and analytics across product lifecycle data.",
      connects: ['Data Science', 'Engineering', 'Mfg', 'Finance'],
      domains: ['AI MCAD', 'AI ECAD', 'AI SW'],
      systems: ['PLM analytics', 'Data lakes', 'BI tools']
    },
    { 
      id: 21,
      name: "Customer & Experience", 
      color: 0xFF6347, 
      speed: 0.0025, 
      description: "Connects customer experience data with product design and development.",
      connects: ['Marketing', 'UX', 'Engineering', 'Service'],
      domains: ['UX design'],
      systems: ['CRM', 'PLM', 'UX tools']
    },
    { 
      id: 22,
      name: "ESG & Compliance Expansion", 
      color: 0x00FF00, 
      speed: 0.0022, 
      description: "Integrates ESG and compliance data across the product lifecycle.",
      connects: ['ESG', 'Exec Mgmt', 'SCM', 'Engineering'],
      domains: ['ESG impacts'],
      systems: ['ESG dashboards', 'PLM ESG', 'ERP']
    }
  ]);

  const [visibleThreadTypes, setVisibleThreadTypes] = useState(
    threadTypes.reduce((acc, thread) => {
      acc[thread.name] = true;
      return acc;
    }, {})
  );

// Create sphere labels with adjustable font size

// Update the createSphereLabel function with text wrapping
const createSphereLabel = useCallback((text, radius, color, fontSize = 20) => {
  const maxWidth = Math.min(256, fontSize * 10);
  const { canvas, width, height } = createTextCanvas(
    text, 
    fontSize, 
    maxWidth, 
    'rgba(0, 0, 0, 0.7)', 
    `#${color.toString(16).padStart(6, '0')}`,
    10
  );
  
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false
  });
  
  const sprite = new THREE.Sprite(material);
  
  // Adjust sprite scale based on content
  const scaleX = width / 25;
  const scaleY = height / 25;
  sprite.scale.set(scaleX, scaleY, 1);
  
  sprite.userData = { 
    text, radius, color, fontSize, texture, material,
    originalScale: { x: scaleX, y: scaleY }
  };
  
  return sprite;
}, []);



// Helper function to draw rounded rectangles
const roundRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};


  // Add sphere labels to the scene
  
// Replace the addSphereLabels function with this corrected version

const addSphereLabels = useCallback((scene, fontSize = 20) => {
  // Store labels in scene for animation access
  scene.userData.sphereLabels = [];
  
  // Domain sphere label
  const domainLabel = createSphereLabel('DOMAINS', 28, 0x8888ff, fontSize);
  domainLabel.position.set(0, 28, 0);
  domainLabel.userData.phaseOffset = 0;
  domainLabel.userData.orbitPlane = 'YZ';
  domainLabel.userData.orbitRadius = 28;
  scene.add(domainLabel);
  scene.userData.sphereLabels.push(domainLabel);
  
  // Function sphere label
  const functionLabel = createSphereLabel('FUNCTIONS', 43, 0xff8888, fontSize);
  functionLabel.position.set(43, 0, 0);
  functionLabel.userData.phaseOffset = Math.PI / 2;
  functionLabel.userData.orbitPlane = 'XZ';
  functionLabel.userData.orbitRadius = 43;
  scene.add(functionLabel);
  scene.userData.sphereLabels.push(functionLabel);
  
  // System sphere label
  const systemLabel = createSphereLabel('SYSTEMS', 58, 0x88ff88, fontSize);
  systemLabel.position.set(0, 30, 58);
  systemLabel.userData.phaseOffset = Math.PI/2;
  systemLabel.userData.orbitPlane = 'XY';
  systemLabel.userData.orbitRadius = 58;
  scene.add(systemLabel);
  scene.userData.sphereLabels.push(systemLabel);
}, [createSphereLabel]);


// Helper function to create department labels with proper font size
/*
const createDepartmentLabel = useCallback((dept, fontSize = 20) => {
  const maxWidth = Math.min(512, fontSize * 15);
  
  // Set background color based on type
  let bgColor;
  if (dept.type === 'domain') {
    bgColor = 'rgba(100, 100, 255, 0.7)';
  } else if (dept.type === 'function') {
    bgColor = 'rgba(255, 100, 100, 0.7)';
  } else {
    bgColor = 'rgba(100, 255, 100, 0.7)';
  }
  
  const { canvas, width, height } = createTextCanvas(
    dept.name,
    fontSize,
    maxWidth,
    bgColor,
    'rgba(255, 255, 255, 1)',
    15
  );
  
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({ 
    map: texture,
    transparent: true
  });
  
  const label = new THREE.Sprite(material);
  label.position.set(0, -2.5, 0);
  
  // Adjust scale based on content size
  const scaleX = width / 50;
  const scaleY = height / 50;
  label.scale.set(scaleX, scaleY, 1);
  
  label.visible = showLabels;
  
  // Store label info for updates
  label.userData = {
    name: dept.name,
    function: dept.function,
    domain: dept.domain,
    system: dept.system,
    type: dept.type,
    texture: texture,
    material: material,
    fontSize: fontSize,
    bgColor: bgColor
  };
  
  return label;
}, []);

*/

// Update the createDepartmentLabel function
const createDepartmentLabel = useCallback((dept, fontSize = 20) => {
  const maxWidth = Math.min(512, fontSize * 15);
  
  // Set background color based on type
  let bgColor;
  if (dept.type === 'domain') {
    bgColor = 'rgba(100, 100, 255, 0.7)';
  } else if (dept.type === 'function') {
    bgColor = 'rgba(255, 100, 100, 0.7)';
  } else {
    bgColor = 'rgba(100, 255, 100, 0.7)';
  }
  
  // Use pill shape for labels
  const { canvas, width, height } = createTextCanvas(
    dept.name,
    fontSize,
    maxWidth,
    bgColor,
    'rgba(255, 255, 255, 1)',
    15,
    true // Enable pill shape
  );
  
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({ 
    map: texture,
    transparent: true
  });
  
  const label = new THREE.Sprite(material);
  label.position.set(0, -2.5, 0);
  
  // Adjust scale based on content size
  const scaleX = width / 50;
  const scaleY = height / 50;
  label.scale.set(scaleX, scaleY, 1);
  
  label.visible = showLabels && (!showOnlyThreadConnectedLabels || threadConnectedLabels.has(dept.id));
  
  // Store label info for updates
  label.userData = {
    name: dept.name,
    function: dept.function,
    domain: dept.domain,
    system: dept.system,
    type: dept.type,
    texture: texture,
    material: material,
    fontSize: fontSize,
    bgColor: bgColor,
    departmentId: dept.id,
    hasThreads: false // Will be updated when threads are created
  };
  
  return label;
}, [showLabels, showOnlyThreadConnectedLabels, threadConnectedLabels]);


// Replace the label animation part in animateParticleSpheres with this corrected version

useEffect(() => {
  if (cameraRef.current) {
    cameraRef.current.zoom = zoomLevel;
    cameraRef.current.updateProjectionMatrix();
  }
}, [zoomLevel]); // This will run whenever zoomLevel changes

  // Create particle spheres
  const createParticleSphere = useCallback((radius, count, color, opacity) => {
    const particlesGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    
    const colorObj = new THREE.Color(color);
    
    for (let i = 0; i < count; i++) {
      // Random position on sphere surface using spherical coordinates
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos((Math.random() * 2) - 1);
      
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);
      
      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
      
      // Random color variations with more vibrant colors
      const r = Math.max(0, Math.min(1, colorObj.r + (Math.random() * 0.4 - 0.2)));
      const g = Math.max(0, Math.min(1, colorObj.g + (Math.random() * 0.4 - 0.2)));
      const b = Math.max(0, Math.min(1, colorObj.b + (Math.random() * 0.4 - 0.2)));
      
      colors[i * 3] = r;
      colors[i * 3 + 1] = g;
      colors[i * 3 + 2] = b;
      
      // Random size with more variation
      sizes[i] = Math.random() * 0.1 + 0.02;
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    particlesGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.05, // Slightly larger particles
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    
    return new THREE.Points(particlesGeometry, particlesMaterial);
  }, []);

 
// Replace the animateParticleSpheres function with this corrected version

const createTextCanvas = (text, fontSize, maxWidth, bgColor, textColor, padding = 10, isPillShape = true) => {
  const lineHeight = fontSize + 4;
  
  // Create canvas with dynamic size
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  
  // Set font to calculate text metrics
  context.font = `Bold ${fontSize}px Arial`;
  
  // Split text into lines that fit within maxWidth
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];
  
  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = context.measureText(currentLine + " " + word).width;
    
    if (width < maxWidth) {
      currentLine += " " + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  
  // Calculate canvas dimensions based on text
  const textWidth = Math.max(...lines.map(line => context.measureText(line).width));
  const canvasWidth = Math.min(maxWidth * 2, Math.max(maxWidth / 2, textWidth + padding * 2));
  const canvasHeight = Math.max(fontSize * 2, lines.length * lineHeight + padding * 2);
  
  // Add extra height for the circular dot if it's a pill shape
  const dotHeight = isPillShape ? fontSize : 0;
  canvas.width = canvasWidth;
  canvas.height = canvasHeight + dotHeight;
  
  // Draw background - either pill shape or rectangle
  context.fillStyle = bgColor;
  
  if (isPillShape) {
    // Draw pill shape (rounded rectangle)
    const pillHeight = canvasHeight;
    const radius = pillHeight / 2;
    
    // Main pill body
    context.beginPath();
    context.moveTo(radius, dotHeight);
    context.lineTo(canvasWidth - radius, dotHeight);
    context.arcTo(canvasWidth, dotHeight, canvasWidth, dotHeight + radius, radius);
    context.lineTo(canvasWidth, dotHeight + pillHeight - radius);
    context.arcTo(canvasWidth, dotHeight + pillHeight, canvasWidth - radius, dotHeight + pillHeight, radius);
    context.lineTo(radius, dotHeight + pillHeight);
    context.arcTo(0, dotHeight + pillHeight, 0, dotHeight + pillHeight - radius, radius);
    context.lineTo(0, dotHeight + radius);
    context.arcTo(0, dotHeight, radius, dotHeight, radius);
    context.closePath();
    context.fill();
    
    // Draw circular dot on top
    const dotRadius = fontSize / 3;
    const dotX = canvasWidth / 2;
    const dotY = dotHeight / 2;
    
    context.beginPath();
    context.arc(dotX, dotY, dotRadius, 0, Math.PI * 2);
    context.fillStyle = textColor;
    context.fill();
  } else {
    // Original rectangle background
    context.fillRect(0, dotHeight, canvasWidth, canvasHeight);
  }
  
  // Draw text with the specified font size
  context.font = `Bold ${fontSize}px Arial`;
  context.fillStyle = textColor;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  
  // Draw text lines (offset by dot height)
  lines.forEach((line, index) => {
    const y = (index * lineHeight) + (lineHeight / 2) + padding + dotHeight;
    context.fillText(line, canvasWidth / 2, y);
  });
  
  return { canvas, width: canvasWidth, height: canvasHeight + dotHeight };
};


 // Animate particle spheres
// Function to update all labels with new font size
const updateLabelFontSizes = useCallback((fontSize) => {
  if (!sceneRef.current || !sceneRef.current.userData.sphereLabels) return;
  
  sceneRef.current.userData.sphereLabels.forEach(label => {
    if (label.userData) {
      // Create new canvas with updated font size
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 256;
      canvas.height = 64;
      
      context.fillStyle = `#${label.userData.color.toString(16).padStart(6, '0')}`;
      context.font = `Bold ${fontSize}px Arial`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(label.userData.text, 128, 32);
      
      context.globalCompositeOperation = 'destination-over';
      context.fillStyle = 'rgba(0, 0, 0, 0.7)';
      context.fillRect(0, 0, 256, 64);
      
      // Update the texture
      label.userData.texture.dispose();
      label.userData.texture = new THREE.CanvasTexture(canvas);
      label.userData.material.map = label.userData.texture;
      label.userData.material.needsUpdate = true;
      label.userData.fontSize = fontSize;
    }
  });
}, []);



// Make sure the animateParticleSpheres function contains the label animation code:
const animateParticleSpheres = useCallback((domainParticles, functionParticles, systemParticles, scene) => {
  // Store original positions for animation
  domainParticles.userData.originalPositions = domainParticles.geometry.attributes.position.array.slice();
  functionParticles.userData.originalPositions = functionParticles.geometry.attributes.position.array.slice();
  systemParticles.userData.originalPositions = systemParticles.geometry.attributes.position.array.slice();
  
  // Set phase offsets for each sphere
  domainParticles.userData.phaseOffset = 0; // Domain: no lag
  functionParticles.userData.phaseOffset = Math.PI / 2; // Function: 90° lag
  systemParticles.userData.phaseOffset = Math.PI; // System: 180° lag
  
  // Add to animation loop
  const spheres = [domainParticles, functionParticles, systemParticles];
  spheres.forEach(sphere => {
    sphere.userData.animate = () => {
      const positions = sphere.geometry.attributes.position.array;
      const originalPositions = sphere.userData.originalPositions;
      const time = Date.now() * 0.001;
      const phaseOffset = sphere.userData.phaseOffset || 0;
      
      for (let i = 0; i < positions.length; i += 3) {
        // Gentle pulsing animation with phase offset
        const pulse = Math.sin(time * 0.5 + i * 0.01 + phaseOffset) * 0.3;
        positions[i] = originalPositions[i] * (1 + pulse * 0.1);
        positions[i + 1] = originalPositions[i + 1] * (1 + pulse * 0.1);
        positions[i + 2] = originalPositions[i + 2] * (1 + pulse * 0.1);
      }
      
      sphere.geometry.attributes.position.needsUpdate = true;
    };
  });
  
  // Add label animation if labels exist
  if (scene && scene.userData && scene.userData.sphereLabels) {
    scene.userData.sphereLabels.forEach(label => {
      label.userData.animate = () => {
        const time = Date.now() * 0.001;
        const phaseOffset = label.userData.phaseOffset || 0;
        const orbitRadius = label.userData.orbitRadius || 28;
        const orbitPlane = label.userData.orbitPlane || 'YZ';
        
        // Calculate orbital position based on phase offset and orbital plane
        const angle = time * 0.3 + phaseOffset;
        
        switch (orbitPlane) {
          case 'XZ': // Function label - orbits in XZ plane
            label.position.x = Math.cos(angle) * orbitRadius;
            label.position.z = Math.sin(angle) * orbitRadius;
            label.position.y = 0;
            break;
          case 'XY': // System label - orbits in XY plane
            label.position.x = Math.cos(angle) * orbitRadius;
            label.position.y = Math.sin(angle) * orbitRadius;
            label.position.z = 0;
            break;
          case 'YZ': // Domain label - orbits in YZ plane
          default:
            label.position.y = Math.cos(angle) * orbitRadius;
            label.position.z = Math.sin(angle) * orbitRadius;
            label.position.x = 0;
            break;
        }
        
        // Always face the camera
        if (cameraRef.current) {
          label.lookAt(cameraRef.current.position);
        }
      };
    });
  }
}, []);


  // Create concentric spheres
const createConcentricSpheres = useCallback((scene, fontSize = 20) => {
  if (!scene) return;
  
  // Clear any existing particle spheres
  particleSpheresRef.current.forEach(sphere => {
    if (sphere && scene.getObjectById(sphere.id)) {
      scene.remove(sphere);
    }
  });
  particleSpheresRef.current = [];
  
  // Clear existing labels if any
  if (scene.userData.sphereLabels) {
    scene.userData.sphereLabels.forEach(label => {
      if (label.userData) {
        label.userData.texture.dispose();
        label.userData.material.dispose();
      }
      scene.remove(label);
    });
  }
  
  // Particle counts for each sphere
  const domainParticleCount = 8000;
  const functionParticleCount = 50000;
  const systemParticleCount = 0;
  
  // Create Domain Sphere (First sphere - blueish particles)
  const domainParticles = createParticleSphere(25, domainParticleCount, 0x8888ff, 0.8);
  domainParticles.userData.isParticleSphere = true;
  scene.add(domainParticles);
  particleSpheresRef.current.push(domainParticles);
  
  // Create Function Sphere (Second sphere - reddish particles)
  const functionParticles = createParticleSphere(40, functionParticleCount, 0xff8888, 0.6);
  functionParticles.userData.isParticleSphere = true;
  scene.add(functionParticles);
  particleSpheresRef.current.push(functionParticles);
  
  // Create System Sphere (Third sphere - greenish particles)
  const systemParticles = createParticleSphere(55, systemParticleCount, 0x88ff88, 0.7);
  systemParticles.userData.isParticleSphere = true;
  scene.add(systemParticles);
  particleSpheresRef.current.push(systemParticles);
  
  // Add glowing effects and animations
  animateParticleSpheres(domainParticles, functionParticles, systemParticles, sceneRef.current);
  
  // Add sphere labels with current font size
  addSphereLabels(scene, labelFontSize);
}, [createParticleSphere, animateParticleSpheres, addSphereLabels]);


  // Initialize Three.js
  const initThreeJS = useCallback(() => {
    if (!containerRef.current) return;
    
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    
    if (width === 0 || height === 0) {
      console.warn('Container has zero dimensions, delaying initialization');
      setTimeout(initThreeJS, 100);
      return;
    }
    
    // Set up scene with proper dark background
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050518);
    scene.fog = new THREE.Fog(0x050518, 100, 200);
    sceneRef.current = scene;

    // Set up camera
    const camera = new THREE.PerspectiveCamera(
      75,
      width / height,
      0.1,
      1000
    );
    camera.position.z = 50;
    cameraRef.current = camera;

    // Set up renderer with more stable configuration
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      powerPreference: "high-performance"
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0); // Ensure transparent background
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Add lights
    const light = new THREE.PointLight(0x00f3ff, 1, 100);
    light.position.set(10, 10, 10);
    scene.add(light);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    // Create central PLM system
    createPLMSystem(scene);

    // Create department constellations
    createConstellations(scene);

    // Create digital threads
    createDigitalThreads(scene);

    // Create starfield background
    createStarfield(scene);

    // Create concentric spheres AFTER everything else
    createConcentricSpheres(scene);

    // Start animation
    animate();
  }, [createConcentricSpheres]);

  const createPLMSystem = (scene) => {
    // Create the PLM system as a pulsating golden core
    const plmGeometry = new THREE.SphereGeometry(6, 32, 32);
    const plmMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFD700,
      transparent: true,
      opacity: 0.2
    });
    
    const plmCore = new THREE.Mesh(plmGeometry, plmMaterial);
    scene.add(plmCore);
    plmCoreRef.current = plmCore;
    
    // Add golden thread spirals around the core
    const goldenThreads = [];
    for (let i = 0; i < 8; i++) {
      const threadGeometry = new THREE.TorusGeometry(8, 0.2, 16, 100);
      const threadMaterial = new THREE.MeshBasicMaterial({
        color: 0xFFD700,
        transparent: true,
        opacity: 0.7
      });
      
      const goldenThread = new THREE.Mesh(threadGeometry, threadMaterial);
      goldenThread.rotation.x = Math.PI / 2;
      goldenThread.rotation.y = (i / 8) * Math.PI * 2;
      
      plmCore.add(goldenThread);
      goldenThreads.push(goldenThread);
    }
    goldenThreadsRef.current = goldenThreads;
    
    // Add intense glow effect
    const glowGeometry = new THREE.SphereGeometry(10, 32, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFD700,
      transparent: true,
      opacity: 0.2
    });
    
    const plmGlow = new THREE.Mesh(glowGeometry, glowMaterial);
    plmCore.add(plmGlow);
    
    // Add PLM label
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 256;
    canvas.height = 64;
    context.fillStyle = 'rgba(255, 215, 0, 0.9)';
    context.font = 'Bold 24px Arial';
    context.textAlign = 'center';
    context.fillText('PLM SYSTEM', 128, 32);
    
    const texture = new THREE.CanvasTexture(canvas);
    const labelMaterial = new THREE.SpriteMaterial({ map: texture });
    const plmLabel = new THREE.Sprite(labelMaterial);
    plmLabel.position.set(0, -8, 0);
    plmLabel.scale.set(12, 3, 1);
    plmCore.add(plmLabel);
  };

  const createConstellations = (scene) => {
    const constellations = [];
    
    departments.forEach((dept) => {
      const constellation = {
        id: dept.id,
        name: dept.name,
        function: dept.function,
        domain: dept.domain,
        system: dept.system,
        type: dept.type,
        position: new THREE.Vector3(...dept.position),
        points: [],
        group: new THREE.Group(),
        label: null,
        dataGenerated: 0,
        dataFlowing: 0
      };

      // Create constellation points (stars)
      const starCount = 3 + Math.floor(Math.random() * 4);
      for (let j = 0; j < starCount; j++) {
        // ... (existing star creation code)
      }
      
      // Add constellation label with enhanced styling for magnifier effect
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 512;  // Double the resolution for better zoom quality
      canvas.height = 128;
      
      // Set different background colors based on type
      if (dept.type === 'domain') {
        context.fillStyle = 'rgba(100, 100, 255, 0.7)'; // Blue for domains
      } else if (dept.type === 'function') {
        context.fillStyle = 'rgba(255, 100, 100, 0.7)'; // Red for functions
      } else {
        context.fillStyle = 'rgba(100, 255, 100, 0.7)'; // Green for systems
      }
      
      context.fillRect(0, 0, 512, 128);
      context.fillStyle = 'rgba(255, 255, 255, 1)';
      context.font = `Bold ${labelFontSize}px Arial`; // Use current font size
      context.textAlign = 'center';
      context.fillText(dept.name, 256, 64);
      
const texture = new THREE.CanvasTexture(canvas);
const labelMaterial = new THREE.SpriteMaterial({ 
  map: texture,
  transparent: true
});
//constellation.label = new THREE.Sprite(labelMaterial);
constellation.label = createDepartmentLabel(dept, labelFontSize);

constellation.label.position.set(0, -2.5, 0);
constellation.label.scale.set(10, 2.5, 1);
constellation.label.visible = showLabels;

// Store label info for updates
constellation.label.userData = {
  name: dept.name,
  function: dept.function,
  domain: dept.domain,
  system: dept.system,
  type: dept.type,
  texture: texture,
  material: labelMaterial,
  fontSize: labelFontSize // Store current font size
};

      
      constellation.group.add(constellation.label);
      constellation.group.position.set(...dept.position);
      
      // Enhanced mouse events with magnifier effect
      constellation.group.userData = { 
        updateLabel: () => {
          if (cameraRef.current && constellation.label) {
            constellation.label.lookAt(cameraRef.current.position);
          }
        },
        constellation: constellation,
        onMouseOver: (event) => {
          const tooltipContent = `
            <strong>${dept.name}</strong><br/>
            Function: ${dept.function}<br/>
            Domain: ${dept.domain}<br/>
            System: ${dept.system}
          `;
          setTooltip({
            visible: true,
            content: tooltipContent,
            x: event.clientX,
            y: event.clientY
          });
          
          // Show magnifier effect
          setMagnifier({
            visible: true,
            content: dept.name,
            x: event.clientX,
            y: event.clientY,
            scale: 1
          });
        },
        onMouseMove: (event) => {
          if (tooltip.visible) {
            setTooltip(prev => ({ 
              ...prev, 
              x: event.clientX, 
              y: event.clientY 
            }));
          }
          
          // Update magnifier position
          setMagnifier(prev => ({
            ...prev,
            x: event.clientX,
            y: event.clientY
          }));
        },
        onMouseOut: () => {
          setTooltip({ visible: false, content: '', x: 0, y: 0 });
          setMagnifier({ visible: false, content: '', x: 0, y: 0, scale: 1 });
        }
      };
      
      scene.add(constellation.group);
      constellations.push(constellation);
    });
       
      constellationsRef.current = constellations;
    };

const createDigitalThreads = (scene) => {
  const digitalThreads = [];
  const connectedDepartmentIds = new Set();
  
  // Create threads based on thread type requirements
  threadTypes.forEach(threadType => {
    // Find all departments that match this thread's requirements
    const functionDepts = departments.filter(dept => 
      threadType.connects.includes(dept.function)
    );
    
    const domainDepts = departments.filter(dept => 
      threadType.domains.includes(dept.domain)
    );
    
    const systemDepts = departments.filter(dept => 
      threadType.systems.includes(dept.system)
    );
    
    // Create 1-2 threads for each type (reduced for performance)
    const threadCount = Math.min(2, Math.max(1, functionDepts.length));
    
    for (let i = 0; i < threadCount; i++) {
      if (functionDepts.length < 1) continue;
      
      // Start with a function department
      const startDept = functionDepts[Math.floor(Math.random() * functionDepts.length)];
      
      // Find a domain department for this thread
      let domainDept = null;
      if (domainDepts.length > 0) {
        domainDept = domainDepts[Math.floor(Math.random() * domainDepts.length)];
      }
      
      // Find a system department for this thread
      let systemDept = null;
      if (systemDepts.length > 0) {
        systemDept = systemDepts[Math.floor(Math.random() * systemDepts.length)];
      }
      
      // Find a different function department to end with
      let endDept = null;
      if (functionDepts.length > 1) {
        do {
          endDept = functionDepts[Math.floor(Math.random() * functionDepts.length)];
        } while (endDept.id === startDept.id);
      }
      
      // Find constellation objects
      const startConstellation = constellationsRef.current.find(c => c.id === startDept.id);
      let domainConstellation = null;
      let systemConstellation = null;
      let endConstellation = null;
      
      if (domainDept) {
        domainConstellation = constellationsRef.current.find(c => c.id === domainDept.id);
      }
      
      if (systemDept) {
        systemConstellation = constellationsRef.current.find(c => c.id === systemDept.id);
      }
      
      if (endDept) {
        endConstellation = constellationsRef.current.find(c => c.id === endDept.id);
      }
      
	      // Track which departments are connected by threads
      if (startConstellation) connectedDepartmentIds.add(startConstellation.id);
      if (domainConstellation) connectedDepartmentIds.add(domainConstellation.id);
      if (systemConstellation) connectedDepartmentIds.add(systemConstellation.id);
      if (endConstellation) connectedDepartmentIds.add(endConstellation.id);

	  
      // Create thread only if we have valid connections
      if (startConstellation && (domainConstellation || systemConstellation || endConstellation)) {
        const threadName = `${threadType.name} Thread`;
        const threadId = `${threadType.id}-${i}-${Date.now()}`;
        
        const connectedDepartments = [startConstellation];
        if (domainConstellation) connectedDepartments.push(domainConstellation);
        if (systemConstellation) connectedDepartments.push(systemConstellation);
        if (endConstellation) connectedDepartments.push(endConstellation);
        
        const thread = {
          type: threadType,
          source: startConstellation,
          target: endConstellation || domainConstellation || systemConstellation,
          particles: [],
          line: null,
          visible: visibleThreadTypes[threadType.name],
          selected: false,
          id: threadId,
          name: threadName,
          connectedDepartments: connectedDepartments,
          intermediatePoints: []
        };
        
        // Add intermediate points for domains and systems
        if (domainConstellation) {
          thread.intermediatePoints.push({
            point: domainConstellation,
            type: 'domain'
          });
        }
        
        if (systemConstellation) {
          thread.intermediatePoints.push({
            point: systemConstellation,
            type: 'system'
          });
        }
        
        // Create visual representation of the thread
        createCompleteThreadVisual(thread, scene);
        digitalThreads.push(thread);
      }
    }
  });

  // Update the state with connected department IDs
  setThreadConnectedLabels(connectedDepartmentIds);
  digitalThreadsRef.current = digitalThreads;
};


// Add this function to toggle label filtering
const toggleThreadConnectedLabels = () => {
  setShowOnlyThreadConnectedLabels(!showOnlyThreadConnectedLabels);
  
  // Update label visibility based on filter
  if (constellationsRef.current) {
    constellationsRef.current.forEach(constellation => {
      if (constellation.label) {
        constellation.label.visible = showLabels && 
          (!showOnlyThreadConnectedLabels || threadConnectedLabels.has(constellation.id));
      }
    });
  }
};


// Enhanced thread visualization that goes through all required points
const createCompleteThreadVisual = (thread, scene) => {
  const points = [];
  
  // Start at source
  points.push(thread.source.position);
  
  // Add intermediate points (domains and systems)
  thread.intermediatePoints.forEach(intermediate => {
    // Create a curved path toward the intermediate point
    const midPoint = new THREE.Vector3().lerpVectors(
      points[points.length - 1], 
      intermediate.point.position, 
      0.5
    );
    
    // Add some randomness to the curve
    midPoint.x += (Math.random() - 0.5) * 8;
    midPoint.y += (Math.random() - 0.5) * 8;
    midPoint.z += (Math.random() - 0.5) * 8;
    
    points.push(midPoint);
    points.push(intermediate.point.position);
  });
  
  // End at target (if exists)
  if (thread.target) {
    const finalMidPoint = new THREE.Vector3().lerpVectors(
      points[points.length - 1], 
      thread.target.position, 
      0.5
    );
    
    // Add some randomness to the final curve
    finalMidPoint.x += (Math.random() - 0.5) * 8;
    finalMidPoint.y += (Math.random() - 0.5) * 8;
    finalMidPoint.z += (Math.random() - 0.5) * 8;
    
    points.push(finalMidPoint);
    points.push(thread.target.position);
  }
  
  // Always pass through the PLM core
  const plmCorePoint = new THREE.Vector3(0, 0, 0);
  points.splice(1, 0, plmCorePoint);
  
  const curve = new THREE.CatmullRomCurve3(points);
  const curvePoints = curve.getPoints(50);
  const geometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
  
  const material = new THREE.LineBasicMaterial({
    color: thread.type.color,
    transparent: true,
    opacity: thread.visible ? 0.4 : 0
  });
  
  thread.line = new THREE.Line(geometry, material);
  
  // Add mouse events for tooltips
  thread.line.userData = {
    onMouseOver: (event) => {
      const tooltipContent = `
        <strong>${thread.type.name}</strong><br/>
        <em>${thread.type.description}</em><br/><br/>
        <strong>Functions:</strong> ${thread.type.connects.join(', ')}<br/>
        <strong>Domains:</strong> ${thread.type.domains.join(', ')}<br/>
        <strong>Systems:</strong> ${thread.type.systems.join(', ')}
      `;
      setTooltip({
        visible: true,
        content: tooltipContent,
        x: event.clientX,
        y: event.clientY
      });
    },
    onMouseOut: () => {
      setTooltip({ visible: false, content: '', x: 0, y: 0 });
    }
  };
  
  scene.add(thread.line);
  
  // Create data particles flowing along the thread
  const particleCount = 12 + Math.floor(Math.random() * 6);
  for (let i = 0; i < particleCount; i++) {
    const particleGeometry = new THREE.SphereGeometry(0.2, 16, 16);
    const particleMaterial = new THREE.MeshBasicMaterial({
      color: thread.type.color,
      transparent: true
    });
    
    const particle = new THREE.Mesh(particleGeometry, particleMaterial);
    particle.visible = thread.visible;
    
    // Store progress along the curve (0 to 1)
    particle.userData = {
      progress: Math.random(),
      speed: thread.type.speed,
      curve: curve,
      toDatabase: true,
      threadId: thread.id
    };
    
    scene.add(particle);
    thread.particles.push(particle);
  }
};

// Enhanced animation for particles moving through all points
const animateParticleThroughPoints = (particle) => {
  const progress = particle.userData.progress;
  const position = particle.userData.curve.getPointAt(progress);
  
  particle.position.copy(position);
  
  // Change color based on what type of point we're near
  const segmentIndex = Math.floor(progress * (particle.userData.curve.points.length - 1));
  if (segmentIndex >= 0 && segmentIndex < particle.userData.curve.points.length) {
    // This is a simplified way to determine what type of point we're near
    // In a real implementation, you'd want to track this more precisely
    if (progress < 0.3) {
      particle.material.color.set(0xff8888); // Function color
    } else if (progress < 0.6) {
      particle.material.color.set(0x8888ff); // Domain color
    } else {
      particle.material.color.set(0x88ff88); // System color
    }
  }
  
  // Update progress
  particle.userData.progress += particle.userData.speed * flowSpeed;
  
  // Reset progress when reaching the end
  if (particle.userData.progress > 1) {
    particle.userData.progress = 0;
    particle.userData.toDatabase = !particle.userData.toDatabase;
  }
};


    const createThreadVisual = (thread, scene) => {
      // Create a flowing line for the thread
      const points = [];
      points.push(thread.source.position);
      
      // Create a curved path that goes toward the PLM system
      const midPoint1 = new THREE.Vector3().lerpVectors(
        thread.source.position, 
        new THREE.Vector3(0, 0, 0), 
        0.33
      );
      
      const midPoint2 = new THREE.Vector3().lerpVectors(
        new THREE.Vector3(0, 0, 0),
        thread.target.position, 
        0.66
      );
      
      // Add some randomness to the curve
      midPoint1.x += (Math.random() - 0.5) * 5;
      midPoint1.y += (Math.random() - 0.5) * 5;
      midPoint1.z += (Math.random() - 0.5) * 5;
      
      midPoint2.x += (Math.random() - 0.5) * 5;
      midPoint2.y += (Math.random() - 0.5) * 5;
      midPoint2.z += (Math.random() - 0.5) * 5;
      
      points.push(midPoint1);
      points.push(new THREE.Vector3(0, 0, 0)); // PLM system
      points.push(midPoint2);
      points.push(thread.target.position);
      
      const curve = new THREE.CatmullRomCurve3(points);
      const curvePoints = curve.getPoints(50);
      const geometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
      
      const material = new THREE.LineBasicMaterial({
        color: thread.type.color,
        transparent: true,
        opacity: thread.visible ? 0.4 : 0
      });
      
      thread.line = new THREE.Line(geometry, material);
      
      // Add mouse events for tooltips
      thread.line.userData = {
        onMouseOver: (event) => {
          const tooltipContent = `
            <strong>${thread.type.name}</strong><br/>
            <em>${thread.type.description}</em><br/><br/>
            <strong>Functions:</strong> ${thread.type.connects.join(', ')}<br/>
            <strong>Domains:</strong> ${thread.type.domains.join(', ')}<br/>
            <strong>Systems:</strong> ${thread.type.systems.join(', ')}
          `;
          setTooltip({
            visible: true,
            content: tooltipContent,
            x: event.clientX,
            y: event.clientY
          });
        },
        onMouseOut: () => {
          setTooltip({ visible: false, content: '', x: 0, y: 0 });
        }
      };
      
      scene.add(thread.line);
      
      // Create data particles flowing along the thread
      const particleCount = 15 + Math.floor(Math.random() * 10);
      for (let i = 0; i < particleCount; i++) {
        const particleGeometry = new THREE.SphereGeometry(0.2, 16, 16);
        const particleMaterial = new THREE.MeshBasicMaterial({
          color: thread.type.color,
          transparent: true
        });
        
        const particle = new THREE.Mesh(particleGeometry, particleMaterial);
        particle.visible = thread.visible;
        
        // Store progress along the curve (0 to 1)
        particle.userData = {
          progress: Math.random(),
          speed: thread.type.speed,
          curve: curve,
          toDatabase: true,
          threadId: thread.id
        };
        
        scene.add(particle);
        thread.particles.push(particle);
      }
    };
  
    const createStarfield = (scene) => {
      // Create background stars
      const starGeometry = new THREE.BufferGeometry();
      const starCount = 2000;
      const positions = new Float32Array(starCount * 3);
      
      for (let i = 0; i < starCount * 3; i += 3) {
        positions[i] = (Math.random() - 0.5) * 2000;
        positions[i+1] = (Math.random() - 0.5) * 2000;
        positions[i+2] = (Math.random() - 0.5) * 2000;
      }
      
      starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      
      const starMaterial = new THREE.PointsMaterial({
        color: 0xffffff,
        size: 0.7,
        transparent: true
      });
      
      const starfield = new THREE.Points(starGeometry, starMaterial);
      scene.add(starfield);
      starfieldRef.current = starfield;
    };
  
    const handleResize = () => {
      if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      const pixelRatio = Math.min(window.devicePixelRatio, 2);
      
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      
      rendererRef.current.setSize(width, height, false);
      rendererRef.current.setPixelRatio(pixelRatio);
    };

// Function to update ALL labels with new font size

// Function to update ALL labels with new font size


// Function to update ALL labels with new font size

// Update the updateAllLabelFontSizes function
const updateAllLabelFontSizes = useCallback((fontSize) => {
  if (!sceneRef.current) return;
  
  // Update main sphere labels (DOMAINS, FUNCTIONS, SYSTEMS)
  if (sceneRef.current.userData.sphereLabels) {
    sceneRef.current.userData.sphereLabels.forEach(label => {
      if (label.userData) {
        const { text, radius, color } = label.userData;
        const maxWidth = Math.min(256, fontSize * 10);
        
        const { canvas, width, height } = createTextCanvas(
          text,
          fontSize,
          maxWidth,
          'rgba(0, 0, 0, 0.7)',
          `#${color.toString(16).padStart(6, '0')}`,
          10,
          true // Enable pill shape for sphere labels too
        );
        
        // Update the texture
        if (label.userData.texture) label.userData.texture.dispose();
        label.userData.texture = new THREE.CanvasTexture(canvas);
        label.userData.material.map = label.userData.texture;
        label.userData.material.needsUpdate = true;
        label.userData.fontSize = fontSize;
        
        // Adjust sprite scale based on content
        const scaleX = width / 25;
        const scaleY = height / 25;
        label.scale.set(scaleX, scaleY, 1);
      }
    });
  }
  
  // Update ALL department constellation labels
  if (constellationsRef.current) {
    constellationsRef.current.forEach(constellation => {
      if (constellation.label && constellation.label.userData) {
        const userData = constellation.label.userData;
        const maxWidth = Math.min(512, fontSize * 15);
        
        const { canvas, width, height } = createTextCanvas(
          userData.name,
          fontSize,
          maxWidth,
          userData.bgColor || 'rgba(100, 100, 255, 0.7)',
          'rgba(255, 255, 255, 1)',
          15,
          true // Enable pill shape
        );
        
        // Update the texture
        if (userData.texture) userData.texture.dispose();
        userData.texture = new THREE.CanvasTexture(canvas);
        userData.material.map = userData.texture;
        userData.material.needsUpdate = true;
        userData.fontSize = fontSize;
        
        // Update visibility based on current filter
        constellation.label.visible = showLabels && 
          (!showOnlyThreadConnectedLabels || threadConnectedLabels.has(constellation.id));
        
        // Adjust label scale based on new content size
        const scaleX = width / 50;
        const scaleY = height / 50;
        constellation.label.scale.set(scaleX, scaleY, 1);
      }
    });
  }
}, [showLabels, showOnlyThreadConnectedLabels, threadConnectedLabels]);


 
    const animate = () => {
      if (!isPlaying) {
        // Still render one frame when paused to show current state
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
          rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
        return;
      }
      
      animationRef.current = requestAnimationFrame(animate);
  // Update camera zoom smoothly
  if (cameraRef.current) {
    cameraRef.current.zoom = cameraZoom;
    cameraRef.current.updateProjectionMatrix();
  }

  // Update camera position with smooth zoom
  if (cameraRef.current) {
    const baseOrbitRadius = 60;
    const effectiveRadius = baseOrbitRadius * (1 / cameraZoom);
    
    cameraRef.current.position.x = Math.sin(Date.now() * 0.0005 * rotationSpeed) * effectiveRadius + panPosition.x;
    cameraRef.current.position.z = Math.cos(Date.now() * 0.0005 * rotationSpeed) * effectiveRadius + panPosition.y;
    cameraRef.current.lookAt(panPosition.x, 0, panPosition.y);
  }

  
      // Animate particle spheres
      if (sceneRef.current) {
        sceneRef.current.children.forEach(child => {
          if (child.userData && child.userData.animate) {
            child.userData.animate();
          }
        });
      }
  
      
      // Update PLM core pulsation
      const pulse = Math.sin(Date.now() * 0.002) * 0.1 + 1;
      if (plmCoreRef.current) {
        plmCoreRef.current.scale.set(pulse, pulse, pulse);
      }
      
      // Update golden threads rotation
      if (goldenThreadsRef.current) {
        goldenThreadsRef.current.forEach(thread => {
          thread.rotation.z += 0.01 * flowSpeed * rotationSpeed;
        });
      }
      
      // Update digital thread particles
      if (digitalThreadsRef.current) {
        digitalThreadsRef.current.forEach(thread => {
          if (thread.visible) {
            thread.particles.forEach(particle => {
              const progress = particle.userData.progress;
              const position = particle.userData.curve.getPointAt(progress);
              
              particle.position.copy(position);
              particle.visible = true;
              
              // Update progress
              particle.userData.progress += particle.userData.speed * flowSpeed;
              
              // Reset progress when reaching the end
              if (particle.userData.progress > 1) {
                particle.userData.progress = 0;
                particle.userData.toDatabase = !particle.userData.toDatabase;
              }
            });
          }
        });
      }
      
      // Rotate starfield slowly
      if (starfieldRef.current) {
        starfieldRef.current.rotation.x += 0.0001 * rotationSpeed;
        starfieldRef.current.rotation.y += 0.0001 * rotationSpeed;
      }
      
      // Update constellation labels to face camera
      if (constellationsRef.current && cameraRef.current) {
        constellationsRef.current.forEach(constellation => {
          if (constellation.userData && constellation.userData.updateLabel) {
            constellation.userData.updateLabel();
          }
        });
      }
      
      // Update camera position for orbiting effect
      if (cameraRef.current) {
        cameraRef.current.position.x = Math.sin(Date.now() * 0.0005 * rotationSpeed) * 60 * zoomLevel + panPosition.x;
        cameraRef.current.position.z = Math.cos(Date.now() * 0.0005 * rotationSpeed) * 60 * zoomLevel + panPosition.y;
        cameraRef.current.lookAt(panPosition.x, 0, panPosition.y);
      }
      
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
	  
	  // Animate sphere labels
if (sceneRef.current && sceneRef.current.userData.sphereLabels) {
  sceneRef.current.userData.sphereLabels.forEach(label => {
    if (label.userData && label.userData.animate) {
      label.userData.animate();
    }
  });
}
    };
  
    const updateThreadVisibility = () => {
      if (digitalThreadsRef.current) {
        digitalThreadsRef.current.forEach(thread => {
          const shouldBeVisible = visibleThreadTypes[thread.type.name];
          thread.visible = shouldBeVisible;
          
          if (thread.line) {
            thread.line.material.opacity = shouldBeVisible ? 0.4 : 0;
          }
          
          thread.particles.forEach(particle => {
            particle.visible = shouldBeVisible;
          });
        });
      }
    };
  
    const updateGlowIntensity = () => {
      if (plmCoreRef.current && plmCoreRef.current.children) {
        plmCoreRef.current.children.forEach(child => {
          if (child.material && child.material.transparent) {
            child.material.opacity = glowIntensity * 0.2;
          }
        });
      }
    };
  
const toggleThreadVisibility = (threadType, threadId = null) => {
  setVisibleThreadTypes(prev => {
    const newVisibility = {
      ...prev,
      [threadType]: !prev[threadType]
    };
    
    // If we have a specific thread ID, handle selection
    if (threadId) {
      const newSelectedThreads = new Set(selectedThreads);
      
      if (newVisibility[threadType]) {
        // Thread is now visible - add to selection
        newSelectedThreads.add(threadId);
      } else {
        // Thread is now hidden - remove from selection
        newSelectedThreads.delete(threadId);
      }
      
      setSelectedThreads(newSelectedThreads);
      
      // Update thread appearance based on selection
      if (digitalThreadsRef.current) {
        digitalThreadsRef.current.forEach(thread => {
          const isSelected = newSelectedThreads.has(thread.id);
          thread.selected = isSelected;
          
          if (thread.line) {
            if (isSelected) {
              thread.line.material.color.set(0xFFFFFF);
              thread.line.material.opacity = 0.8;
            } else {
              thread.line.material.color.set(thread.type.color);
              thread.line.material.opacity = thread.visible ? 0.4 : 0;
            }
          }
          
          thread.particles.forEach(particle => {
            if (isSelected) {
              particle.scale.set(1.5, 1.5, 1.5);
              particle.material.opacity = 1;
            } else {
              particle.scale.set(1, 1, 1);
              particle.material.opacity = thread.visible ? 0.7 : 0;
            }
          });
        });
      }
    }
    
    return newVisibility;
  });
};

  
    const toggleAllThreads = (visible) => {
      const newVisibility = {};
      threadTypes.forEach(thread => {
        newVisibility[thread.name] = visible;
      });
      setVisibleThreadTypes(newVisibility);
    };
  

// Update the toggleThreadSelection function to properly handle selection:
const toggleThreadSelection = (threadId) => {
  const newSelectedThreads = new Set(selectedThreads);
  
  if (newSelectedThreads.has(threadId)) {
    newSelectedThreads.delete(threadId);
    console.log(`Deselected thread: ${threadId}`);
  } else {
    newSelectedThreads.add(threadId);
    console.log(`Selected thread: ${threadId}`);
  }
  
  setSelectedThreads(newSelectedThreads);
  
  // Debug: Log current selection state
  console.log('Current selected threads:', Array.from(newSelectedThreads));
  
  // Update thread appearance based on selection
  if (digitalThreadsRef.current) {
    digitalThreadsRef.current.forEach(thread => {
      const isSelected = newSelectedThreads.has(thread.id);
      thread.selected = isSelected;
      
      if (thread.line) {
        if (isSelected) {
          thread.line.material.color.set(0xFFFFFF);
          thread.line.material.opacity = 0.8;
        } else {
          thread.line.material.color.set(thread.type.color);
          thread.line.material.opacity = thread.visible ? 0.4 : 0;
        }
        thread.line.material.needsUpdate = true;
      }
      
      // Also update particles for selected threads
      thread.particles.forEach(particle => {
        if (isSelected) {
          particle.scale.set(1.5, 1.5, 1.5);
          particle.material.opacity = 1;
        } else {
          particle.scale.set(1, 1, 1);
          particle.material.opacity = thread.visible ? 0.7 : 0;
        }
      });
    });
  }
};

// Add a function to get selected thread details

// Enhanced version that combines B's functionality with A's quantum concepts
/*
const getSelectedThreadDetails = () => {
  if (!digitalThreadsRef.current || selectedThreads.size === 0) return [];
  
  return digitalThreadsRef.current
    .filter(thread => selectedThreads.has(thread.id))
    .map(thread => ({
      // Core thread data (from Function B)
      id: thread.id,
      name: thread.name,
      type: thread.type?.name || 'unknown',
      color: thread.type?.color || '#666',
      source: thread.source?.name || 'unknown',
      target: thread.target?.name || 'unknown',
      
      // Quantum enhancement data (inspired by Function A)
      quantumMetrics: calculateThreadQuantumMetrics(thread),
      optimizationPriority: calculateOptimizationPriority(thread),
      connectedLifecycles: findConnectedLifecycles(thread.id),
      criticalFactors: identifyCriticalFactorsForThread(thread),
      
      // Additional useful properties
      healthScore: calculateThreadHealth(thread),
      optimizationPotential: assessOptimizationPotential(thread)
    }));
};

*/

// You'll need to implement these helper functions:
const calculateThreadQuantumMetrics = (thread) => {
  return {
    coherence: 70 + Math.random() * 30,
    connectivity: 60 + Math.random() * 40,
    valuePotential: 65 + Math.random() * 35,
    optimizationScore: 60 + Math.random() * 40
  };
};

const calculateOptimizationPriority = (thread) => {
  const score = Math.random() * 100;
  if (score > 80) return 'HIGH';
  if (score > 50) return 'MEDIUM';
  return 'LOW';
};

const calculateThreadHealth = (thread) => {
  return 60 + Math.random() * 40; // 60-100%
};

const assessOptimizationPotential = (thread) => {
  return 50 + Math.random() * 50; // 50-100%
};

const findConnectedLifecycles = (threadId) => {
  // Find which lifecycles connect to this thread
  return []; // Array of lifecycle IDs or objects
};

const identifyCriticalFactorsForThread = (thread) => {
  // Identify which quantum factors are critical for this thread
  return []; // Array of factor keys
};

/*
const getSelectedThreadDetails = () => {
  if (!digitalThreadsRef.current || selectedThreads.size === 0) return [];
  
  return digitalThreadsRef.current
    .filter(thread => selectedThreads.has(thread.id))
    .map(thread => ({
      id: thread.id,
      name: thread.name,
      type: thread.type.name,
      color: thread.type.color,
      source: thread.source.name,
      target: thread.target.name
    }));
};

*/

// Add a button to process selected threads
/* 
const processSelectedThreads = () => {
  const selectedThreadDetails = getSelectedThreadDetails();
  if (selectedThreadDetails.length === 0) {
    alert("No threads selected for processing");
    return;
  }
  
  // Here you can implement what to do with the selected threads
  console.log("Processing selected threads:", selectedThreadDetails);
  
  // Example: Show an alert with the selected threads
  alert(`Processing ${selectedThreadDetails.length} selected threads:\n\n${
    selectedThreadDetails.map(t => `• ${t.name} (${t.type})`).join('\n')
  }`);
}; 
*/

// Quantum Thread Processing Engine - Enhanced Implementation
const processSelectedThreads = () => {
  const selectedThreadDetails = getSelectedThreadDetails();
  
  if (selectedThreadDetails.length === 0) {
    showQuantumNotification("No threads selected for quantum processing", "warning");
    return;
  }

  // Initialize Quantum Optimization Session
  const optimizationSession = initializeQuantumOptimization(selectedThreadDetails);
  
  // Launch Quantum Optimization Interface
  launchQuantumOptimizer(optimizationSession);
};

// Start : MANDAR

// Core Quantum Calculation Functions
const calculateInitialQuantumMetrics = (threads) => {
  const metrics = {
    quantumCoherence: 0,
    threadConnectivity: 0,
    valueCreationPotential: 0,
    digitalContinuity: 0,
    riskMitigation: 0,
    innovationCapacity: 0,
    sustainabilityIndex: 0,
    systemInteroperability: 0
  };

  threads.forEach(thread => {
    // Calculate based on thread properties and connected lifecycles
    const threadMetrics = analyzeThreadQuantumMetrics(thread);
    Object.keys(metrics).forEach(metric => {
      metrics[metric] += threadMetrics[metric] || 0;
    });
  });

  // Normalize metrics
  Object.keys(metrics).forEach(metric => {
    metrics[metric] = Math.min(100, Math.max(0, metrics[metric] / threads.length));
  });

  return metrics;
};

const mapThreadLifecycleConnections = (threads) => {
  const connections = [];
  
  threads.forEach(thread => {
    const connectedLifecycles = findConnectedLifecycles(thread.id);
    const criticalFactors = identifyCriticalFactorsForThread(thread);
    
    connections.push({
      threadId: thread.id,
      threadName: thread.name,
      connectedLifecycles,
      criticalFactors,
      optimizationPotential: calculateOptimizationPotential(thread, connectedLifecycles),
      currentHealth: calculateThreadHealth(thread, connectedLifecycles)
    });
  });
  
  return connections;
};

const generateOptimizationGoals = (threads) => {
  const goals = [];
  
  threads.forEach(thread => {
    const threadGoals = {
      threadId: thread.id,
      threadName: thread.name,
      shortTerm: generateShortTermGoals(thread),
      mediumTerm: generateMediumTermGoals(thread),
      longTerm: generateLongTermGoals(thread),
      expectedImpact: calculateExpectedImpact(thread),
      implementationComplexity: assessImplementationComplexity(thread)
    };
    
    goals.push(threadGoals);
  });
  
  return goals;
};

// Real-time Optimization Monitoring
const startOptimizationMonitoring = (session) => {
  const monitoringInterval = setInterval(() => {
    updateRealTimeMetrics(session);
    checkOptimizationProgress(session);
    adjustRecommendations(session);
  }, 2000); // Update every 2 seconds

  session.monitoringInterval = monitoringInterval;
};

const updateRealTimeMetrics = (session) => {
  const currentMetrics = calculateCurrentQuantumMetrics(session);
  updateMetricsDisplay(currentMetrics);
  triggerQuantumInterferenceEvents(currentMetrics);
};

// Interactive Control Handlers
const initializeQuantumControls = (modal, session) => {
  // Optimize Critical Lifecycles
  modal.querySelector('#optimizeCriticalBtn').addEventListener('click', () => {
    optimizeCriticalLifecycles(session);
  });

  // Apply Recommendations
  modal.querySelector('#applyRecommendationsBtn').addEventListener('click', () => {
    applySelectedRecommendations(session);
  });

  // Export Report
  modal.querySelector('#exportReportBtn').addEventListener('click', () => {
    exportQuantumOptimizationReport(session);
  });

  // Close Optimizer
  modal.querySelector('#closeOptimizerBtn').addEventListener('click', () => {
    completeOptimizationSession(session, modal);
  });

  // Close Modal
  modal.querySelector('.close-quantum-modal').addEventListener('click', () => {
    completeOptimizationSession(session, modal);
  });
};

// Advanced Optimization Functions
const optimizeCriticalLifecycles = (session) => {
  showQuantumNotification("Optimizing critical lifecycles...", "info");
  
  const criticalLifecycles = identifyCriticalLifecycles(session);
  
  criticalLifecycles.forEach(lifecycle => {
    applyLifecycleOptimization(lifecycle, session);
  });
  
  updateSessionMetrics(session);
  showQuantumNotification("Critical lifecycle optimization complete!", "success");
};

const applySelectedRecommendations = (session) => {
  const selectedRecommendations = getSelectedRecommendations();
  
  if (selectedRecommendations.length === 0) {
    showQuantumNotification("No recommendations selected", "warning");
    return;
  }

  showQuantumNotification(`Applying ${selectedRecommendations.length} recommendations...`, "info");
  
  selectedRecommendations.forEach(recommendation => {
    executeRecommendation(recommendation, session);
  });
  
  updateSessionMetrics(session);
  generateOptimizationReport(session);
};

// Utility Functions
const showQuantumNotification = (message, type = "info") => {
  const notification = document.createElement('div');
  notification.className = `quantum-notification ${type}`;
  notification.innerHTML = `
    <div class="notification-content">
      <span class="notification-icon">${getNotificationIcon(type)}</span>
      <span class="notification-message">${message}</span>
    </div>
  `;
  
  document.body.appendChild(notification);
  
  setTimeout(() => {
    notification.remove();
  }, 5000);
};

const getNotificationIcon = (type) => {
  const icons = {
    info: "ℹ️",
    success: "✅",
    warning: "⚠️",
    error: "❌"
  };
  return icons[type] || "ℹ️";
};

const exportQuantumOptimizationReport = (session) => {
  const report = generateComprehensiveReport(session);
  
  // Create downloadable report
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `quantum_optimization_report_${session.sessionId}.json`;
  a.click();
  
  showQuantumNotification("Quantum optimization report exported!", "success");
};

const completeOptimizationSession = (session, modal) => {
  // Stop monitoring
  if (session.monitoringInterval) {
    clearInterval(session.monitoringInterval);
  }
  
  // Generate final report
  const finalReport = generateFinalOptimizationReport(session);
  
  // Save session results
  saveOptimizationSession(session, finalReport);
  
  // Remove modal
  modal.remove();
  
  showQuantumNotification(`Optimization session completed! ${session.optimizationMetrics.improvementCount} improvements applied.`, "success");
};

// CSS Styles for Quantum Optimizer (to be added to your stylesheet)
const injectQuantumStyles = () => {
  const styles = `
    .quantum-optimization-modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    .quantum-modal-overlay {
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(10px);
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }
    
    .quantum-modal-container {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 20px;
      width: 95%;
      height: 90%;
      max-width: 1400px;
      display: flex;
      flex-direction: column;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    }
    
    .quantum-modal-header {
      background: rgba(255, 255, 255, 0.95);
      padding: 20px 30px;
      border-radius: 20px 20px 0 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    
    .quantum-modal-body {
      flex: 1;
      padding: 30px;
      overflow-y: auto;
    }
    
    .optimization-panels {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto;
      gap: 20px;
      height: 100%;
    }
    
    .panel {
      background: rgba(255, 255, 255, 0.95);
      border-radius: 15px;
      padding: 20px;
      box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
    }
    
    .quantum-action-buttons {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 15px;
      margin-top: 30px;
    }
    
    .quantum-notification {
      position: fixed;
      top: 20px;
      right: 20px;
      background: white;
      padding: 15px 20px;
      border-radius: 10px;
      box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
      z-index: 10001;
      border-left: 4px solid;
    }
    
    .quantum-notification.success { border-left-color: #2ecc71; }
    .quantum-notification.info { border-left-color: #3498db; }
    .quantum-notification.warning { border-left-color: #f39c12; }
    .quantum-notification.error { border-left-color: #e74c3c; }
    
    .metrics-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 10px;
    }
    
    .metric-item {
      background: linear-gradient(135deg, #f8f9ff, #f0f2ff);
      padding: 15px;
      border-radius: 10px;
      text-align: center;
    }
    
    .metric-value {
      font-size: 1.5rem;
      font-weight: bold;
      color: #667eea;
    }
    
    .metric-label {
      font-size: 0.8rem;
      color: #666;
      margin-top: 5px;
    }
  `;
  
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
};

// Initialize Quantum Styles on First Run
if (!document.querySelector('#quantum-styles')) {
  injectQuantumStyles();
}

// Enhanced getSelectedThreadDetails function with quantum analysis
const getSelectedThreadDetails = () => {
  if (!digitalThreadsRef.current || selectedThreads.size === 0) return [];
  
  // Filter threads that are in the selectedThreads Set and map to details
  const selectedDetails = digitalThreadsRef.current
    .filter(thread => selectedThreads.has(thread.id))
    .map(thread => {
      // Get the source and target names safely
      const sourceName = thread.source?.name || 'Unknown Source';
      const targetName = thread.target?.name || 'Unknown Target';
      
      return {
        id: thread.id,
        name: thread.name || `Thread ${thread.id}`,
        type: thread.type?.name || 'Digital Thread',
        color: thread.type?.color || 0x1E90FF,
        source: sourceName,
        target: targetName,
        description: thread.type?.description || 'Digital thread connecting departments',
        // Add quantum metrics
        quantumMetrics: calculateThreadQuantumMetrics(thread),
        optimizationPriority: calculateOptimizationPriority(thread),
        connectedDepartments: thread.connectedDepartments?.map(dept => dept.name) || [],
        healthScore: Math.random() * 100, // Placeholder - implement your logic
        optimizationPotential: Math.random() * 100 // Placeholder - implement your logic
      };
    });
  
  console.log('Selected thread details:', selectedDetails); // Debug log
  return selectedDetails;
};


// End : MANDAR


    const focusOnPLM = () => {
      if (cameraRef.current) {
        cameraRef.current.position.set(0, 0, 60);
        cameraRef.current.lookAt(0, 0, 0);
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
      }
    };
  
    const toggleExplosionView = () => {
      setIsExplosionView(!isExplosionView);
      
      if (constellationsRef.current) {
        constellationsRef.current.forEach(constellation => {
          if (isExplosionView) {
            // Return to original positions
            constellation.group.position.set(...departments.find(d => d.id === constellation.id).position);
          } else {
            // Move constellations further out based on type
            const factor = constellation.type === 'system' ? 2.0 : constellation.type === 'function' ? 1.5 : 1.2;
            constellation.group.position.multiplyScalar(factor);
          }
        });
      }
    };
  
// Update the toggleLabels function
const toggleLabels = () => {
  const newShowLabels = !showLabels;
  setShowLabels(newShowLabels);
  
  if (constellationsRef.current) {
    constellationsRef.current.forEach(constellation => {
      if (constellation.label) {
        constellation.label.visible = newShowLabels && 
          (!showOnlyThreadConnectedLabels || threadConnectedLabels.has(constellation.id));
      }
    });
  }
};
	
	
  
    const setGoldenThread = (threadType) => {
      setGoldenThreadType(threadType);
      
      if (digitalThreadsRef.current) {
        digitalThreadsRef.current.forEach(thread => {
          const isGolden = thread.type.name === threadType;
          
          if (thread.line) {
            if (isGolden) {
              thread.line.material.color.set(0xFFD700);
            } else {
              thread.line.material.color.set(thread.type.color);
            }
          }
          
          thread.particles.forEach(particle => {
            if (isGolden) {
              particle.material.color.set(0xFFD700);
              particle.scale.set(1.5, 1.5, 1.5);
            } else {
              particle.material.color.set(thread.type.color);
              particle.scale.set(1, 1, 1);
            }
          });
        });
      }
    };
  
    const showOnlySelectedThreads = () => {
      if (digitalThreadsRef.current) {
        digitalThreadsRef.current.forEach(thread => {
          const shouldShow = selectedThreads.has(thread.id);
          thread.visible = shouldShow;
          
          if (thread.line) {
            thread.line.material.opacity = shouldShow ? 0.4 : 0;
          }
          
          thread.particles.forEach(particle => {
            particle.visible = shouldShow;
          });
        });
      }
    };
  
    const toggleFullScreen = () => {
      if (!document.fullscreenElement) {
        containerRef.current.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable full-screen mode: ${err.message}`);
        });
        setIsFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setIsFullScreen(false);
        }
      }
    };
  
// Enhanced zoom functions
const zoomIn = () => {
  setZoomLevel(prev => {
    const newZoom = Math.min(prev + 0.1, 3);
    setCameraZoom(newZoom);
    return newZoom;
  });
};
  
const zoomOut = () => {
  setZoomLevel(prev => {
    const newZoom = Math.max(prev - 0.1, 0.3);
    setCameraZoom(newZoom);
    return newZoom;
  });
};

    const panLeft = () => {
      setPanPosition(prev => ({ ...prev, x: prev.x - 5 }));
    };

    const panRight = () => {
      setPanPosition(prev => ({ ...prev, x: prev.x + 5 }));
    };

    const panUp = () => {
      setPanPosition(prev => ({ ...prev, y: prev.y - 5 }));
    };

    const panDown = () => {
      setPanPosition(prev => ({ ...prev, y: prev.y + 5 }));
    };

    const resetVisualization = () => {
      // Reset all states to default
      setIsPlaying(true);
      setFlowSpeed(1);
      setThreadIntensity(1);
      setGlowIntensity(1);
      setShowLabels(true);
      setIsExplosionView(false);
      setSelectedThreads(new Set());
      setGoldenThreadType(null);
      setRotationSpeed(0.5);
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });
	  
	    // Reset camera
  if (cameraRef.current) {
    cameraRef.current.zoom = 1;
    cameraRef.current.updateProjectionMatrix();
    cameraRef.current.position.set(0, 0, 60);
    cameraRef.current.lookAt(0, 0, 0);
  }
      
      // Reset thread visibility
      const defaultVisibility = {};
      threadTypes.forEach(thread => {
        defaultVisibility[thread.name] = true;
      });
      setVisibleThreadTypes(defaultVisibility);
      
      // Reset camera position
      focusOnPLM();
      
      // Reset constellations to original positions
      if (constellationsRef.current) {
        constellationsRef.current.forEach(constellation => {
          constellation.group.position.set(...departments.find(d => d.id === constellation.id).position);
        });
      }
      
      // Reset thread appearances
      if (digitalThreadsRef.current) {
        digitalThreadsRef.current.forEach(thread => {
          thread.visible = true;
          
          if (thread.line) {
            thread.line.material.color.set(thread.type.color);
            thread.line.material.opacity = 0.4;
          }
          
          thread.particles.forEach(particle => {
            particle.visible = true;
            particle.material.color.set(thread.type.color);
            particle.scale.set(1, 1, 1);
          });
        });
      }
    };

    const addCustomThread = () => {
      if (!newThreadName || selectedFunctions.size === 0) return;
      
      // Convert hex color to number
      const colorNum = parseInt(newThreadColor.replace('#', ''), 16);
      
      const newThread = {
        id: threadTypes.length + 1,
        name: newThreadName,
        color: colorNum,
        speed: 0.002,
        description: "Custom digital thread created by user",
        connects: Array.from(selectedFunctions),
        domains: ['Custom'],
        systems: ['Custom System']
      };
      
      setThreadTypes([...threadTypes, newThread]);
      
      // Add to visible threads
      setVisibleThreadTypes(prev => ({
        ...prev,
        [newThreadName]: true
      }));
      
      // Set as golden thread
      setGoldenThreadType(newThreadName);
      
      // Close modal and reset form
      setCustomThreadModal(false);
      setNewThreadName('');
      setNewThreadColor('#1E90FF');
      setSelectedFunctions(new Set());
    };

    const addCustomDepartment = () => {
      // This would open another modal to add a custom department
      alert("Custom department feature would be implemented here");
    };

    const toggleFunctionSelection = (func) => {
      const newSelectedFunctions = new Set(selectedFunctions);
      if (newSelectedFunctions.has(func)) {
        newSelectedFunctions.delete(func);
      } else {
        newSelectedFunctions.add(func);
      }
      setSelectedFunctions(newSelectedFunctions);
    };

  // Add this function to handle mouse move with magnifier
  const handleMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
    }
    
	      {magnifier.visible && (
        <div style={{
          position: 'fixed',
          top: magnifier.y - 60,
          left: magnifier.x + 20,
          background: 'rgba(10, 10, 42, 0.95)',
          color: '#e0e0ff',
          padding: '15px',
          borderRadius: '10px',
          border: '2px solid rgba(0, 243, 255, 0.7)',
          zIndex: 101,
          minWidth: '200px',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 20px rgba(0, 243, 255, 0.5)',
          transform: `scale(${magnifier.scale})`,
          transformOrigin: 'top left',
          transition: 'transform 0.3s ease',
          pointerEvents: 'none'
        }}
        onMouseEnter={() => setMagnifier(prev => ({ ...prev, scale: 1.1 }))}
        onMouseLeave={() => setMagnifier(prev => ({ ...prev, scale: 1 }))}
        >
          <div style={{
            fontSize: '1.4em',
            fontWeight: 'bold',
            marginBottom: '10px',
            background: 'linear-gradient(to right, #00f3ff, #ffd700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {magnifier.content}
          </div>
          <div style={{ fontSize: '0.9em', opacity: 0.9 }}>
            Click for details
          </div>
        </div>
      )}
	
    if (magnifier.visible) {
      setMagnifier(prev => ({
        ...prev,
        x: e.clientX,
        y: e.clientY
      }));
    }
  };


/*     // Handle mouse move for tooltip
    const handleMouseMove = (e) => {
      if (tooltip.visible) {
        setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
      }
    };
 */
    // Initialize Three.js after component mounts
    useLayoutEffect(() => {
      try {
        setIsLoading(true);
        initThreeJS();
        
        // Handle window resize
        window.addEventListener('resize', handleResize);
        
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
        console.error("Error initializing 3D visualization:", err);
      }
      
      return () => {
        // Cleanup function
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        if (rendererRef.current) {
          rendererRef.current.dispose();
          if (containerRef.current && containerRef.current.contains(rendererRef.current.domElement)) {
            containerRef.current.removeChild(rendererRef.current.domElement);
          }
        }
        window.removeEventListener('resize', handleResize);
      };
    }, [initThreeJS]);

    // Control animation based on isPlaying state
    useEffect(() => {
      if (isPlaying) {
        animate();
      } else {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
      }
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }, [isPlaying]);

    // Handle thread visibility changes
    useEffect(() => {
      updateThreadVisibility();
    }, [visibleThreadTypes]);

    // Handle glow intensity changes
    useEffect(() => {
      updateGlowIntensity();
    }, [glowIntensity]);

    if (error) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '600px',
          color: '#ff6b6b',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div>
            <h2>Error Loading Visualization</h2>
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                background: '#00f3ff',
                color: '#050518',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ 
        position: 'relative', 
        width: '100%', 
        height: '100vh', 
        overflow: 'hidden',
        background: 'linear-gradient(to bottom, #050518 0%, #0a0a2a 100%)'
      }} onMouseMove={handleMouseMove}>
        {/* Canvas container for Three.js visualization */}
        <div 
          ref={containerRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'transparent',
            border: '1px solid rgba(0, 243, 255, 0.3)',
            borderRadius: '10px',
            overflow: 'hidden'
          }}
        />
        
        {/* Loading overlay */}
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(5, 5, 24, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 100,
            color: '#00f3ff',
            fontSize: '24px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '20px' }}>Loading Digital Thread Visualization...</div>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid rgba(0, 243, 255, 0.3)',
                borderTop: '4px solid #00f3ff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          </div>
        )}
        
        {/* Tooltip */}
        {tooltip.visible && (
          <div style={{
            position: 'fixed',
            top: tooltip.y + 10,
            left: tooltip.x + 10,
            background: 'rgba(10, 10, 42, 0.95)',
            color: '#e0e0ff',
            padding: '10px',
            borderRadius: '5px',
            border: '1px solid rgba(0, 243, 255, 0.5)',
            zIndex: 100,
            maxWidth: '300px',
            backdropFilter: 'blur(10px)',
            pointerEvents: 'none'
          }}>
            <div dangerouslySetInnerHTML={{ __html: tooltip.content }} />
          </div>
        )}
        
        {/* eEvolve Watermark */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 5,
          opacity: 0.03,
          pointerEvents: 'none',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: '8em',
            fontWeight: 900,
            background: 'linear-gradient(to right, #00f3ff, #ffd700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 30px rgba(0, 243, 255, 0.5)',
            margin: 0
          }}>eEvolve</h1>
          <p style={{
            fontSize: '1.5em',
            marginTop: '-20px',
            color: 'rgba(255, 255, 255, 0.5)'
          }}>The Lumina Thread: Your Digital Transformation</p>
        </div>
        
{/* Top controls bar */}
{showTopPanel && (
  <div style={{
    position: 'absolute',
    top: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '10px',
    background: 'rgba(10, 10, 42, 0.8)',
    padding: '10px 15px',
    borderRadius: '30px',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(0, 243, 255, 0.3)',
    zIndex: 10,
    transition: 'all 0.3s ease'
  }}>
    <button 
      onClick={() => setIsPlaying(!isPlaying)}
      style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '20px', padding: '5px 10px' }}
    >
      {isPlaying ? '⏸️ Pause' : '▶️ Resume'}
    </button>
    <button 
      onClick={focusOnPLM}
      style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '20px', padding: '5px 10px' }}
    >
      🔍 Focus PLM
    </button>
    <button 
      onClick={toggleExplosionView}
      style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '20px', padding: '5px 10px' }}
    >
      {isExplosionView ? '🔗 Collapse' : '🔗 Explode'}
    </button>
    <button 
      onClick={toggleLabels}
      style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '20px', padding: '5px 10px' }}
    >
      {showLabels ? '🏷️ Hide Labels' : '🏷️ Show Labels'}
    </button>
    <button 
      onClick={showOnlySelectedThreads}
      style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '20px', padding: '5px 10px' }}
    >
      👁️ Show Selected
    </button>
    <button 
      onClick={toggleFullScreen}
      style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '20px', padding: '5px 10px' }}
    >
      {isFullScreen ? '📱 Exit Fullscreen' : '🖥️ Fullscreen'}
    </button>
  <button 
  onClick={processSelectedThreads}
  disabled={selectedThreads.size === 0}
  style={{ 
    color: selectedThreads.size > 0 ? '#00f3ff' : '#666',
    background: 'transparent', 
    border: '1px solid rgba(0, 243, 255, 0.3)', 
    borderRadius: '20px', 
    padding: '5px 10px',
    cursor: selectedThreads.size > 0 ? 'pointer' : 'not-allowed'
  }}
>
  ⚙️ Process Selected ({selectedThreads.size})
</button>
          <button 
            onClick={resetVisualization}
            style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '20px', padding: '5px 10px' }}
          >
            🔄 Reset
          </button>
          <button 
            onClick={toggleFullScreen}
            style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '20px', padding: '5px 10px' }}
          >
            {isFullScreen ? '📱 Exit Fullscreen' : '🖥️ Fullscreen'}
          </button>
		  <button 
  onClick={toggleThreadConnectedLabels}
  style={{ 
    color: showOnlyThreadConnectedLabels ? '#FFD700' : '#00f3ff', 
    background: showOnlyThreadConnectedLabels ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
    border: `1px solid ${showOnlyThreadConnectedLabels ? 'rgba(255, 215, 0, 0.5)' : 'rgba(0, 243, 255, 0.3)'}`, 
    borderRadius: '20px', 
    padding: '5px 10px' 
  }}
>
  {showOnlyThreadConnectedLabels ? '🔵 Show All Labels' : '⚪ Thread Labels Only'}
</button>
   </div>
)}



{/* Toggle top panel button */}
<button 
  onClick={() => setShowTopPanel(!showTopPanel)}
  style={{
    position: 'absolute',
    top: '10px',
    left: '10px',
    color: '#00f3ff',
    background: 'rgba(10, 10, 42, 0.8)',
    border: '1px solid rgba(0, 243, 255, 0.3)',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 11,
    cursor: 'pointer',
    backdropFilter: 'blur(10px)'
  }}
>
  {showTopPanel ? '▲' : '▼'}
</button>

        
        {/* Zoom and Pan controls */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          background: 'rgba(10, 10, 42, 0.8)',
          padding: '10px',
          borderRadius: '10px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 243, 255, 0.3)',
          zIndex: 10
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <button 
              onClick={zoomIn}
              style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '5px', width: '40px', height: '40px' }}
            >
              ➕
            </button>
            <span style={{ color: '#e0e0ff', fontSize: '12px' }}>Zoom</span>
            <button 
              onClick={zoomOut}
              style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '5px', width: '40px', height: '40px' }}
            >
              ➖
            </button>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px' }}>
            <div></div>
            <button 
              onClick={panUp}
              style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '5px', width: '40px', height: '40px' }}
            >
              ↑
            </button>
            <div></div>
            
            <button 
              onClick={panLeft}
              style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '5px', width: '40px', height: '40px' }}
            >
              ←
            </button>
            <div style={{ width: '40px', height: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#e0e0ff', fontSize: '12px' }}>
              Pan
            </div>
            <button 
              onClick={panRight}
              style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '5px', width: '40px', height: '40px' }}
            >
              →
            </button>
            
            <div></div>
            <button 
              onClick={panDown}
              style={{ color: '#00f3ff', background: 'transparent', border: '1px solid rgba(0, 243, 255, 0.3)', borderRadius: '5px', width: '40px', height: '40px' }}
            >
              ↓
            </button>
            <div></div>
          </div>
        </div>
        
        {/* Toggle controls button */}
        <button 
          onClick={() => setShowControls(!showControls)}
          style={{
            position: 'absolute',
            top: '70px',
            left: '20px',
            color: '#00f3ff',
            background: 'rgba(10, 10, 42, 0.8)',
            border: '1px solid rgba(0, 243, 255, 0.3)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 11,
            cursor: 'pointer'
          }}
        >
          {showControls ? '◀' : '▶'}
        </button>
        
        {/* Control panel */}
        {showControls && (
          <div style={{
            position: 'absolute',
            top: '70px',
            left: '70px',
            background: 'rgba(10, 10, 42, 0.8)',
            padding: '15px',
            borderRadius: '10px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 243, 255, 0.3)',
            zIndex: 10,
            width: '250px'
          }}>
            <h3 style={{ color: '#00f3ff', marginBottom: '15px' }}>Visualization Controls</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0ff' }}>Flow Speed</label>
              <input 
                type="range" 
                min="0.1" 
                max="3" 
                step="0.1" 
                value={flowSpeed}
                onChange={(e) => setFlowSpeed(parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
<div style={{ marginBottom: '15px' }}>
  <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0ff' }}>Label Font Size</label>
  <input 
    type="range" 
    min="30" 
    max="90" 
    step="1" 
    value={labelFontSize}
    onChange={(e) => {
      const newSize = parseInt(e.target.value);
      setLabelFontSize(newSize);
      // Use requestAnimationFrame to ensure smooth updates
      requestAnimationFrame(() => {
        updateAllLabelFontSizes(newSize);
      });
    }}
    style={{ width: '100%' }}
  />
  <div style={{ color: '#e0e0ff', fontSize: '12px', textAlign: 'center' }}>
    {labelFontSize}px
  </div>
</div>


    
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0ff' }}>Glow Intensity</label>
              <input 
                type="range" 
                min="0.5" 
                max="3" 
                step="0.1" 
                value={glowIntensity}
                onChange={(e) => setGlowIntensity(parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0ff' }}>Rotation Speed</label>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.05" 
                value={rotationSpeed}
                onChange={(e) => setRotationSpeed(parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
            
            <button 
              onClick={() => setCustomThreadModal(true)}
              style={{
                width: '100%',
                padding: '8px',
                background: 'transparent',
                color: '#00f3ff',
                border: '1px solid rgba(0, 243, 255, 0.3)',
                borderRadius: '5px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              + Create Custom Thread
            </button>

            <button 
              onClick={addCustomDepartment}
              style={{
                width: '100%',
                padding: '8px',
                background: 'transparent',
                color: '#00f3ff',
                border: '1px solid rgba(0, 243, 255, 0.3)',
                borderRadius: '5px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              + Add Custom Department
            </button>
          </div>
        )}
        
        {/* Toggle thread panel button */}
        <button 
          onClick={() => setShowThreadPanel(!showThreadPanel)}
          style={{
            position: 'absolute',
            top: '70px',
            right: '20px',
            color: '#00f3ff',
            background: 'rgba(10, 10, 42, 0.8)',
            border: '1px solid rgba(0, 243, 255, 0.3)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 11,
            cursor: 'pointer'
          }}
        >
          {showThreadPanel ? '▶' : '◀'}
        </button>
        
        {/* Thread controls panel */}
        {showThreadPanel && (
          <div style={{
            position: 'absolute',
            top: '70px',
            right: '70px',
            background: 'rgba(10, 10, 42, 0.8)',
            padding: '15px',
            borderRadius: '10px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 243, 255, 0.3)',
            zIndex: 10,
            maxHeight: '70%',
            overflowY: 'auto',
            width: '300px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
              <h3 style={{ color: '#00f3ff', margin: 0 }}>Digital Threads</h3>
              <div>
                <button 
                  onClick={() => toggleAllThreads(true)}
                  style={{ 
                    color: '#00f3ff', 
                    background: 'transparent', 
                    border: '1px solid rgba(0, 243, 255, 0.3)', 
                    borderRadius: '5px', 
                    padding: '2px 5px',
                    fontSize: '12px',
                    marginRight: '5px'
                  }}
                >
                  All
                </button>
                <button 
                  onClick={() => toggleAllThreads(false)}
                  style={{ 
                    color: '#00f3ff', 
                    background: 'transparent', 
                    border: '1px solid rgba(0, 243, 255, 0.3)', 
                    borderRadius: '5px', 
                    padding: '2px 5px',
                    fontSize: '12px'
                  }}
                >
                  None
                </button>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
{threadTypes.map((thread) => {
  // Generate a consistent ID for this thread type (using the first instance)
  const threadInstances = digitalThreadsRef.current?.filter(t => t.type.name === thread.name) || [];
  const threadId = threadInstances.length > 0 ? threadInstances[0].id : `${thread.id}-0`;
  
  return (
    <div key={thread.id} style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px',
      opacity: visibleThreadTypes[thread.name] ? 1 : 0.6,
      padding: '5px',
      background: thread.name === goldenThreadType ? 'rgba(255, 215, 0, 0.1)' : 
                 (selectedThreads.has(threadId) ? 'rgba(0, 243, 255, 0.1)' : 'transparent'),
      borderRadius: '5px',
      border: thread.name === goldenThreadType ? '1px solid rgba(255, 215, 0, 0.5)' : 
              (selectedThreads.has(threadId) ? '1px solid rgba(0, 243, 255, 0.3)' : 'none'),
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      const tooltipContent = `
        <strong>${thread.name}</strong><br/>
        <em>${thread.description}</em><br/><br/>
        <strong>Functions:</strong> ${thread.connects.join(', ')}<br/>
        <strong>Domains:</strong> ${thread.domains.join(', ')}<br/>
        <strong>Systems:</strong> ${thread.systems.join(', ')}
      `;
      setTooltip({
        visible: true,
        content: tooltipContent,
        x: e.clientX,
        y: e.clientY
      });
    }}
    onMouseLeave={() => {
      setTooltip({ visible: false, content: '', x: 0, y: 0 });
    }}
    >
      <button 
        onClick={() => toggleThreadVisibility(thread.name, threadId)}
        style={{ 
          color: visibleThreadTypes[thread.name] ? `#${thread.color.toString(16).padStart(6, '0')}` : '#a0a0ff',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px'
        }}
      >
        {visibleThreadTypes[thread.name] ? '👁️' : '👁️‍🗨️'}
      </button>
      
      <div style={{ 
        width: '12px', 
        height: '12px', 
        borderRadius: '50%', 
        background: `#${thread.color.toString(16).padStart(6, '0')}`,
        boxShadow: thread.name === goldenThreadType ? `0 0 8px #${thread.color.toString(16).padStart(6, '0')}` : 'none'
      }} />
      
      <span style={{ 
        color: thread.name === goldenThreadType ? '#FFD700' : '#e0e0ff',
        fontWeight: (thread.name === goldenThreadType || selectedThreads.has(threadId)) ? 'bold' : 'normal',
        fontSize: '14px',
        flex: 1
      }}>
        {thread.name}
        {thread.name === goldenThreadType && ' (Golden)'}
        {selectedThreads.has(threadId) && ' ✓'}
      </span>
      
      <button 
        onClick={() => setGoldenThread(thread.name)}
        style={{ 
          color: thread.name === goldenThreadType ? '#FFD700' : '#a0a0ff',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px'
        }}
      >
        {thread.name === goldenThreadType ? '⭐' : '☆'}
      </button>
    </div>
  );
})}

			</div>

            <h4 style={{ color: '#00f3ff', marginTop: '15px', marginBottom: '10px' }}>Selected Threads</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {digitalThreadsRef.current && digitalThreadsRef.current
                .filter(thread => selectedThreads.has(thread.id))
                .map(thread => (
                  <div key={thread.id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '5px',
                    fontSize: '12px',
                    color: '#e0e0ff'
                  }}>
                    <div style={{ 
                      width: '10px', 
                      height: '10px', 
                      borderRadius: '50%', 
                      background: `#${thread.type.color.toString(16).padStart(6, '0')}`
                    }} />
                    <span>{thread.name}</span>
                  </div>
                ))
              }
              {selectedThreads.size === 0 && (
                <span style={{ fontSize: '12px', color: '#a0a0ff' }}>No threads selected</span>
              )}
            </div>
          </div>
        )}
        
        {/* Custom Thread Modal */}
        {customThreadModal && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(10, 10, 42, 0.95)',
            padding: '20px',
            borderRadius: '10px',
            border: '2px solid rgba(0, 243, 255, 0.5)',
            zIndex: 20,
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ color: '#00f3ff', marginBottom: '15px' }}>Create Custom Digital Thread</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0ff' }}>Thread Name</label>
              <input 
                type="text" 
                value={newThreadName}
                onChange={(e) => setNewThreadName(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(0, 243, 255, 0.3)',
                  borderRadius: '5px',
                  color: 'white'
                }}
                placeholder="Enter thread name"
              />
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0ff' }}>Thread Color</label>
              <input 
                type="color" 
                value={newThreadColor}
                onChange={(e) => setNewThreadColor(e.target.value)}
                style={{ 
                  width: '100%', 
                  height: '40px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(0, 243, 255, 0.3)',
                  borderRadius: '5px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#e0e0ff' }}>Select Functions to Connect</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {allFunctions.map(func => (
                  <button
                    key={func}
                    onClick={() => toggleFunctionSelection(func)}
                    style={{
                      padding: '5px 10px',
                      background: selectedFunctions.has(func) ? '#00f3ff' : 'rgba(0, 243, 255, 0.2)',
                      color: selectedFunctions.has(func) ? '#050518' : '#e0e0ff',
                      border: '1px solid rgba(0, 243, 255, 0.3)',
                      borderRadius: '15px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    {func}
                  </button>
                ))}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setCustomThreadModal(false)}
                style={{
                  padding: '8px 15px',
                  background: 'transparent',
                  color: '#ff6b6b',
                  border: '1px solid rgba(255, 107, 107, 0.3)',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={addCustomThread}
                disabled={!newThreadName || selectedFunctions.size === 0}
                style={{
                  padding: '8px 15px',
                  background: selectedFunctions.size > 0 ? '#00f3ff' : 'rgba(0, 243, 255, 0.3)',
                  color: selectedFunctions.size > 0 ? '#050518' : 'rgba(255, 255, 255, 0.5)',
                  border: '1px solid rgba(0, 243, 255, 0.3)',
                  borderRadius: '5px',
                  cursor: selectedFunctions.size > 0 ? 'pointer' : 'not-allowed'
                }}
              >
                Create Thread
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  export default PLMVisualization;